/**
 * Estimate Form Component
 * Modal form for creating and editing work estimates with BOQ items
 * Supports multiple locations/options per proposal with item-wise quantity.
 * Design files are NOT uploaded here — they belong to the work and are uploaded in
 * Works > Designs. This form shows them read-only so the estimator can price against them.
 */

import '@/modules/utility/ds-wms/styles/wmsTable.css';
import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { estimatesApi, proposalsApi, mastersApi, contractorsApi } from '../../services/wmsService';
import { useWorkMasterList } from '../../hooks/useWorkMasterList';
import axiosInstance from '@/services/interceptor';
import { useToast } from '@/hooks/useToast';
import SORSelector, { type SORItem as SORSelectorItem } from '../SORSelector';
import RateContractSelector, { type RateContractItem } from '../RateContractSelector';
import { useEstimateTypes } from '../../hooks/useSetupMasters';
import { WmsUdaSelect } from '../common/WmsUdaScope';
import { ListSkeleton } from '@/components/base/Skeleton';
import GoodsItemFields, { useAssetCategories } from './GoodsItemFields';
import { normaliseProcurementType, procurementTypeLabel } from '../../lib/procurementType';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

// Unique ID generator (counter-based, no collisions)
let _uid = Date.now();
const uid = () => ++_uid;

// Types
interface ItemGroup {
  grp_id: number;
  grp_nm: string;
  sqnce_nu: number;
  collapsed?: boolean;
}

interface WorkOption {
  optn_id: number;
  optn_nm: string;
  lctn_tx: string;
  /** Files queued for THIS location, not yet uploaded. Uploaded ones are read back
   *  from the work's drawings (see LOCATION_TAG) rather than duplicated into state. */
  dsgn_files: LocationDesignFile[];
  items: BOQItem[];
  itemGroups: ItemGroup[];
  totl_at: number;
}

interface BOQItem {
  itm_id: number;
  itm_cd: string;
  itm_nm: string;
  itm_dscn_tx: string;
  itm_type_cd: 'SOR' | 'RATE_CONTRACT' | 'INDIVIDUAL';
  sor_itm_id: number | null;
  rate_cntrct_id: number | null;
  uom_cd: string;
  uom_nm: string;
  qty_nu: number;
  rate_at: number;
  amount_at: number;
  rmrks_tx: string;
  grp_id?: number; // Item group ID (null = ungrouped)
  // Schedule-of-Rates provenance, read back from the saved line rather than retyped:
  // which SOR year and type the rate item belongs to, and the sanctioned rate. The
  // sanctioned rate is kept separate from rate_at on purpose — rate_at is what this
  // estimate actually used, and the gap between them is the variance worth seeing.
  sor_yr_cd?: string;
  sor_type_nm?: string;
  sor_rte_at?: number | null;
  // Goods purchase (server migration 217): the asset category the line becomes when
  // its bill is paid (null = not an asset), and the item specification.
  asst_ctgry_id?: number | null;
  spcfcn_tx?: string;
}

interface AbstractEstimateItem {
  abstrt_itm_id: number;
  dscn_tx: string;
  amount_at: number;
}

/**
 * A drawing already stored against the WORK, rendered read-only on this form.
 *
 * Upload/delete happen in exactly one place — Works > Designs (DesignForm) — which writes
 * dswms_drwng_lst_t. This form previously carried a second, identical-looking dropzone that
 * never uploaded anything (see the read-only panel below), which is why two screens appeared
 * to accept the same BIM/CAD/GIS/PDF files.
 *
 * No file size field: the read path that serves these rows selects NULL for it, so showing a
 * size would render "0 KB" for every file.
 */
interface WorkDrawing {
  drwng_id: number;
  drwng_nm: string | null;
  file_nm: string | null;
  file_url: string | null;
  drwng_type_cd: string | null;
  drwng_type_nm: string | null;
  dscn_tx: string | null;
}

/**
 * A file queued against one estimate OPTION (= one location), waiting to be uploaded when
 * the estimate is saved. Once uploaded it stops living here and is read back from the
 * work's drawings, so there is exactly one source of truth for stored files.
 */
interface LocationDesignFile {
  file_id: number;
  file_nm: string;
  file_obj: File;
  file_type_cd: string;
  file_sz: number;
  uploading: boolean;
  error?: string;
}

/**
 * Location designs and work designs share one store (dswms_drwng_lst_t, keyed by wrk_id) —
 * there is no per-option drawing table. The option is recorded in the drawing's description
 * with this prefix, which is what separates "the design for Option 2" from the rest of the
 * work's drawings, and what makes the origin readable in the Designs screen.
 */
const LOCATION_TAG = (optionName: string) => `Location: ${optionName}`;

const DRAWING_TYPE_STYLES: Record<string, { icon: string; chip: string }> = {
  BIM: { icon: 'ri-building-3-line text-blue-600', chip: 'bg-blue-100 text-blue-700' },
  CAD: { icon: 'ri-pencil-ruler-2-line text-purple-600', chip: 'bg-purple-100 text-purple-700' },
  GIS: { icon: 'ri-map-2-line text-teal-600', chip: 'bg-teal-100 text-teal-700' },
  PDF: { icon: 'ri-file-pdf-line text-red-600', chip: 'bg-red-100 text-red-700' },
  IMAGE: { icon: 'ri-image-line text-green-600', chip: 'bg-green-100 text-green-700' },
  OTHER: { icon: 'ri-file-line text-gray-600', chip: 'bg-gray-100 text-gray-700' }
};

// drwng_type_cd arrives from a LEFT JOIN and is frequently null, so fall back to the file
// extension using the same buckets the Designs screen uses.
function drawingType(typeCd: string | null | undefined, fileName: string | null | undefined): string {
  const cd = (typeCd || '').toUpperCase();
  if (DRAWING_TYPE_STYLES[cd]) return cd;
  const name = fileName || '';
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  if (['.ifc', '.rvt', '.nwd', '.nwc', '.nwf', '.dgn'].includes(ext)) return 'BIM';
  if (['.dwg', '.dxf', '.dwf', '.dws'].includes(ext)) return 'CAD';
  if (['.kml', '.kmz', '.shp', '.shx', '.dbf', '.prj', '.geojson', '.gpx'].includes(ext)) return 'GIS';
  if (ext === '.pdf') return 'PDF';
  if (['.jpg', '.jpeg', '.png', '.gif', '.tiff', '.bmp'].includes(ext)) return 'IMAGE';
  return 'OTHER';
}

interface SORItem {
  sor_itm_id: number;
  sor_itm_cd: string;
  sor_itm_nm: string;
  sor_itm_dscn_tx: string;
  uom_cd: string;
  uom_nm: string;
  rate_at: number;
  ctgry_nm: string;
}

// RateContractItem imported from RateContractSelector
interface _RateContractItemLocal {
  rate_cntrct_id: number;
  rate_cntrct_cd: string;
  rate_cntrct_nm: string;
  uom_cd: string;
  uom_nm: string;
  rate_at: number;
  cntrctr_nm: string;
}

interface UOM {
  uom_id: number;
  uom_cd: string;
  uom_nm: string;
  uom_type_cd?: string;
}

interface Proposal {
  prpsl_id: number;
  prpsl_cd: string;
  prpsl_nm: string;
  estmt_cst_at: number;
  wrk_type_nm: string;
  schme_nm: string;
  lctn_tx: string;
  zone_nm: string;
  ward_nm: string;
}

interface EstimateFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  workId?: number | null;
  estimateId?: number | null;
  initialEstimateType?: 'ORIGINAL' | 'REVISED' | 'SUPPLEMENTARY';
}

const initialOption: WorkOption = {
  optn_id: 0,
  optn_nm: 'Option 1',
  lctn_tx: '',
  dsgn_files: [],
  items: [],
  itemGroups: [],
  totl_at: 0
};

const initialBOQItem: BOQItem = {
  itm_id: 0,
  itm_cd: '',
  itm_nm: '',
  itm_dscn_tx: '',
  itm_type_cd: 'INDIVIDUAL',
  sor_itm_id: null,
  rate_cntrct_id: null,
  uom_cd: '',
  uom_nm: '',
  qty_nu: 0,
  rate_at: 0,
  amount_at: 0,
  rmrks_tx: '',
  sor_yr_cd: '',
  sor_type_nm: '',
  sor_rte_at: null
};

/**
 * Tooltip text for a BOQ line taken from the Schedule of Rates.
 *
 * Names the SOR year and type the rate item belongs to, and — when the estimate used a
 * rate different from the sanctioned one — states both and the variance. A silent
 * difference between the SOR rate and the rate an estimate actually used is exactly the
 * thing a technical sanction is supposed to catch, so it is spelled out rather than left
 * to whoever thinks to divide amount by quantity.
 */
function sorProvenance(item: BOQItem): string {
  const parts: string[] = [];
  if (item.sor_yr_cd) parts.push(`SOR ${item.sor_yr_cd}`);
  if (item.sor_type_nm) parts.push(item.sor_type_nm);
  if (item.itm_cd) parts.push(`Item ${item.itm_cd}`);

  const sanctioned = item.sor_rte_at;
  if (sanctioned !== null && sanctioned !== undefined) {
    const used = item.rate_at || 0;
    const fmt = (n: number) => `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    if (Math.abs(used - sanctioned) < 0.005) {
      parts.push(`Rate ${fmt(sanctioned)} (as sanctioned)`);
    } else {
      const delta = used - sanctioned;
      const pct = sanctioned !== 0 ? ` (${delta > 0 ? '+' : ''}${((delta / sanctioned) * 100).toFixed(1)}%)` : '';
      parts.push(`Sanctioned ${fmt(sanctioned)}, used ${fmt(used)}${pct}`);
    }
  }
  return parts.length ? parts.join(' · ') : 'Schedule of Rates item';
}

// Section Header Component
function SectionHeader({ icon, title, action }: { icon: string; title: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3 px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-500 rounded-lg">
      <div className="flex items-center gap-2">
        <i className={`${icon} text-white`}></i>
        <h4 className="font-medium text-white">{title}</h4>
      </div>
      {action}
    </div>
  );
}

export default function EstimateForm({ isOpen, onClose, onSuccess, workId, estimateId, initialEstimateType }: EstimateFormProps) {
  // From Setup -> Estimate Types (dswms_estmt_typ_lst_t), NATURE axis only: this field
  // asks how the estimate relates to earlier ones, so the DETAIL axis (Preliminary /
  // Detailed) does not belong in it.
  const estimateTypes = useEstimateTypes('NATURE');

  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Form data
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [workDetails, setWorkDetails] = useState<any>(null);
  // The work this estimate already belongs to, read off the estimate itself.
  // Edit mode never loads workDetails (it has no workId prop to load it from), so
  // without this the save path had no work id at all — see resolvedWorkId below.
  const [estimateWrkId, setEstimateWrkId] = useState<number | null>(null);
  const [estimateNm, setEstimateNm] = useState('');
  const [estimateTypeCd, setEstimateTypeCd] = useState<'ORIGINAL' | 'REVISED' | 'SUPPLEMENTARY'>('ORIGINAL');
  // Dimension fields (optional) — live on the estimate independent of the work
  const [estmtAdmnUntId, setEstmtAdmnUntId] = useState<number | null>(null);
  const [estmtWrkTypeId, setEstmtWrkTypeId] = useState<number | null>(null);
  const [estmtWrkCtgryId, setEstmtWrkCtgryId] = useState<number | null>(null);
  const [estmtSchmeId, setEstmtSchmeId] = useState<number | null>(null);
  const [ulbsList, setUlbsList] = useState<Array<{ id: number; nm: string }>>([]);
  const [options, setOptions] = useState<WorkOption[]>([{ ...initialOption, optn_id: uid() }]);
  const [activeOptionIdx, setActiveOptionIdx] = useState(0);
  const [rmrksTx, setRmrksTx] = useState('');

  // Abstract Estimate
  const [abstractItems, setAbstractItems] = useState<AbstractEstimateItem[]>([]);
  const [isAbstractExpanded, setIsAbstractExpanded] = useState(true);
  const abstractTotal = abstractItems.reduce((sum, item) => sum + item.amount_at, 0);

  // Masters
  const [uoms, setUoms] = useState<UOM[]>([]);
  const [sorItems, setSorItems] = useState<SORItem[]>([]);
  const [rateContractItems, setRateContractItems] = useState<RateContractItem[]>([]);

  // Existing estimates already linked to the picked work (used to prevent duplicates)
  interface ExistingEstimate {
    estmt_id: number; estmt_cd: string; estmt_nm: string; estmt_type_cd: string;
    estmt_sts_cd?: string; sts_cd?: string; totl_at: number; prep_dt?: string | null;
  }
  const [existingEstimates, setExistingEstimates] = useState<ExistingEstimate[]>([]);

  // Attached proposals (M:M with the work)
  interface AttachedProposal {
    prpsl_id: number; prpsl_cd: string; prpsl_nm: string; estmt_cst_at: number;
    prpsl_sts_cd: string; prpsl_dt: string | null; lctn_tx: string | null;
  }
  const [attachedProposals, setAttachedProposals] = useState<AttachedProposal[]>([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [showProposalPicker, setShowProposalPicker] = useState(false);
  const [proposalPickerSearch, setProposalPickerSearch] = useState('');
  const [proposalPickerResults, setProposalPickerResults] = useState<AttachedProposal[]>([]);
  const [proposalPickerLoading, setProposalPickerLoading] = useState(false);
  const [proposalPickerSelected, setProposalPickerSelected] = useState<Set<number>>(new Set());
  const proposalPickerSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Item picker
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [showSORSelector, setShowSORSelector] = useState(false);
  const [showRCSelector, setShowRCSelector] = useState(false);
  // Individual item form state (React-controlled instead of DOM)
  const [indvItem, setIndvItem] = useState({ itm_cd: '', itm_nm: '', itm_dscn_tx: '', uom_group: '', uom_cd: '', qty_nu: '1', rate_at: '' });

  // Item group management
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [dragItem, setDragItem] = useState<{ itemIdx: number } | null>(null);

  const addItemGroup = () => {
    if (!newGroupName.trim()) return;
    const newOptions = [...options];
    const groups = newOptions[activeOptionIdx].itemGroups || [];
    groups.push({ grp_id: uid(), grp_nm: newGroupName.trim(), sqnce_nu: groups.length + 1 });
    newOptions[activeOptionIdx].itemGroups = groups;
    setOptions(newOptions);
    setNewGroupName('');
    setShowAddGroup(false);
  };

  const removeItemGroup = (grpId: number) => {
    const newOptions = [...options];
    newOptions[activeOptionIdx].itemGroups = (newOptions[activeOptionIdx].itemGroups || []).filter(g => g.grp_id !== grpId);
    // Ungroup items that were in this group
    newOptions[activeOptionIdx].items = newOptions[activeOptionIdx].items.map(i => i.grp_id === grpId ? { ...i, grp_id: undefined } : i);
    setOptions(newOptions);
  };

  const toggleGroupCollapse = (grpId: number) => {
    const newOptions = [...options];
    newOptions[activeOptionIdx].itemGroups = (newOptions[activeOptionIdx].itemGroups || []).map(g =>
      g.grp_id === grpId ? { ...g, collapsed: !g.collapsed } : g
    );
    setOptions(newOptions);
  };

  const moveItemToGroup = (itemIdx: number, grpId: number | undefined) => {
    const newOptions = [...options];
    newOptions[activeOptionIdx].items[itemIdx] = { ...newOptions[activeOptionIdx].items[itemIdx], grp_id: grpId };
    setOptions(newOptions);
  };

  const getGroupTotal = (grpId: number) => {
    return activeOption.items.filter(i => i.grp_id === grpId).reduce((s, i) => s + i.amount_at, 0);
  };

  const getGroupItemCount = (grpId: number) => {
    return activeOption.items.filter(i => i.grp_id === grpId).length;
  };

  const getUngroupedItems = () => {
    const groupIds = new Set((activeOption.itemGroups || []).map(g => g.grp_id));
    return activeOption.items.filter(i => !i.grp_id || !groupIds.has(i.grp_id));
  };
  const [itemPickerType, setItemPickerType] = useState<'SOR' | 'RATE_CONTRACT' | 'INDIVIDUAL' | 'FROM_WORK'>('SOR');
  const [itemSearchTerm, setItemSearchTerm] = useState('');

  // "From Rate Contract" is only offered when rate contracts actually exist. Nothing in
  // the app creates them yet, so on most installs the tab would open onto a permanently
  // empty list. One cheap probe (one row, we only read the count) decides it; the tab
  // returns by itself once contracts are loaded, with no code change needed.
  const [hasRateContracts, setHasRateContracts] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('ds-wms/rate-contract-items/picker', { params: { limit: 1 } });
        if (!cancelled) setHasRateContracts((res.data?.pagination?.total || 0) > 0);
      } catch {
        if (!cancelled) setHasRateContracts(false); // unreachable or erroring: stay hidden
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Guard against landing on a tab that is no longer offered.
  useEffect(() => {
    if (!hasRateContracts && itemPickerType === 'RATE_CONTRACT') setItemPickerType('SOR');
  }, [hasRateContracts, itemPickerType]);

  // Import from existing work
  const [importWorkSearch, setImportWorkSearch] = useState('');
  const [importWorkResults, setImportWorkResults] = useState<any[]>([]);
  const [importWorkLoading, setImportWorkLoading] = useState(false);
  const [importSelectedWork, setImportSelectedWork] = useState<any>(null);
  const [importWorkItems, setImportWorkItems] = useState<any[]>([]);
  const [importItemsLoading, setImportItemsLoading] = useState(false);
  const importSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (itemPickerType !== 'FROM_WORK' || !importWorkSearch) return;
    if (importSearchTimeout.current) clearTimeout(importSearchTimeout.current);
    importSearchTimeout.current = setTimeout(async () => {
      setImportWorkLoading(true);
      try {
        const { worksListApi } = await import('../../services/wmsService');
        const result = await worksListApi.getAll({ search: importWorkSearch } as any, 1, 10);
        setImportWorkResults(result.data || []);
      } catch (e) { console.error('Import work search failed:', e); }
      finally { setImportWorkLoading(false); }
    }, 300);
    return () => { if (importSearchTimeout.current) clearTimeout(importSearchTimeout.current); };
  }, [importWorkSearch, itemPickerType]);

  const selectImportWork = async (work: any) => {
    setImportSelectedWork(work);
    setImportItemsLoading(true);
    try {
      const response = await axiosInstance.get(`ds-wms/estimates/work/${work.wrk_id}/items`);
      setImportWorkItems(response.data.data || []);
    } catch (e) { console.error('Failed to load work items:', e); setImportWorkItems([]); }
    finally { setImportItemsLoading(false); }
  };

  const importAllItems = () => {
    if (importWorkItems.length === 0) return;
    const newOptions = [...options];
    importWorkItems.forEach((item: any) => {
      newOptions[activeOptionIdx].items.push({
        ...initialBOQItem,
        itm_id: uid(),
        itm_cd: item.itm_cd || '', itm_nm: item.itm_nm || '', itm_dscn_tx: item.itm_dscn_tx || '',
        itm_type_cd: item.itm_type_cd || 'SOR', sor_itm_id: null, rate_cntrct_id: null,
        uom_cd: item.uom_cd || '', uom_nm: item.uom_nm || item.uom_cd || '',
        rate_at: parseFloat(item.rate_at) || 0, qty_nu: parseFloat(item.qty_nu) || 0,
        amount_at: (parseFloat(item.qty_nu) || 0) * (parseFloat(item.rate_at) || 0)
      });
    });
    newOptions[activeOptionIdx].totl_at = newOptions[activeOptionIdx].items.reduce((s, i) => s + i.amount_at, 0);
    setOptions(newOptions);
    setImportSelectedWork(null); setImportWorkItems([]); setImportWorkSearch(''); setShowItemPicker(false);
  };

  const isEdit = !!estimateId;

  // Work-master lists for the dimension dropdowns (work type, category, scheme, FY)
  const { workTypes, workCategories, schemes } = useWorkMasterList();

  // Load master data
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [uomsData, sorData] = await Promise.all([
          mastersApi.getUOMs?.() || [],
          contractorsApi.getSORItems?.() || []
        ]);
        setUoms(uomsData || []);
        setSorItems(sorData || []);
      } catch (error) {
        console.error('Error loading masters:', error);
      }
    };
    if (isOpen) {
      loadMasters();
    }
  }, [isOpen]);

  // Load UDAs (optional dimension) — resolved via admin-units master
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const data = await mastersApi.getAdminUnits(true, 'UDA');
        setUlbsList(((data || []) as any[]).map((u: any) => ({ id: u.admn_unt_id ?? u.ulb_id, nm: u.admn_unt_nm ?? u.ulb_nm })));
      } catch (e) {
        console.error('Error loading UDAs:', e);
      }
    })();
  }, [isOpen]);

  // Load existing estimates for the current work (to surface and prevent duplicates)
  const loadExistingEstimates = useCallback(async (wrkId: number | null | undefined) => {
    if (!wrkId) { setExistingEstimates([]); return; }
    try {
      const result = await estimatesApi.getAll({ workId: wrkId } as any, 1, 100);
      const all = (result.data || []) as ExistingEstimate[];
      // Exclude the one currently being edited so the form doesn't flag itself
      const filtered = estimateId ? all.filter((e) => e.estmt_id !== estimateId) : all;
      setExistingEstimates(filtered);
    } catch (e) {
      console.error('Error loading existing estimates for work:', e);
      setExistingEstimates([]);
    }
  }, [estimateId]);

  // Derived: would Save create a duplicate ORIGINAL?
  const hasOriginalEstimate = existingEstimates.some((e) => e.estmt_type_cd === 'ORIGINAL');
  const blockOriginalDuplicate = !isEdit && estimateTypeCd === 'ORIGINAL' && hasOriginalEstimate;

  // Load proposals attached to a work (M:M)
  const loadAttachedProposals = useCallback(async (wrkId: number | null | undefined) => {
    if (!wrkId) { setAttachedProposals([]); return; }
    setLoadingProposals(true);
    try {
      const { worksApi } = await import('../../services/wmsService');
      const list = await worksApi.getProposals(wrkId);
      setAttachedProposals(list || []);
      // Seed legacy single-proposal context if not already populated
      setProposal((prev) => {
        if (prev) return prev;
        const first = (list || [])[0];
        return first ? {
          prpsl_id: first.prpsl_id,
          prpsl_cd: first.prpsl_cd,
          prpsl_nm: first.prpsl_nm,
          estmt_cst_at: first.estmt_cst_at || 0,
          wrk_type_nm: '', schme_nm: '', lctn_tx: first.lctn_tx || '', zone_nm: '', ward_nm: ''
        } : prev;
      });
    } catch (e) {
      console.error('Error loading attached proposals:', e);
    } finally {
      setLoadingProposals(false);
    }
  }, []);

  // Resolve the work id used for proposal links (edit mode loads it via estimate)
  const effectiveWrkId: number | null = workDetails?.workId ?? estimateWrkId ?? workId ?? null;

  // Procurement type of the bound work (server migration 217). Read from the work
  // itself because an estimate opened for edit never loads workDetails.
  const [wrkProcurementType, setWrkProcurementType] = useState<string>('WORKS');
  useEffect(() => {
    if (!isOpen || !effectiveWrkId) { setWrkProcurementType('WORKS'); return; }
    if (workDetails?.workId === effectiveWrkId && workDetails?.procurementType) {
      setWrkProcurementType(normaliseProcurementType(workDetails.procurementType));
      return;
    }
    let alive = true;
    import('../../services/wmsService')
      .then(({ worksApi }) => worksApi.getById(effectiveWrkId))
      .then((w: any) => { if (alive) setWrkProcurementType(normaliseProcurementType(w?.procurementType)); })
      .catch(() => { /* stays WORKS: no goods fields, nothing is lost */ });
    return () => { alive = false; };
  }, [isOpen, effectiveWrkId, workDetails]);
  const isGoodsWork = wrkProcurementType === 'GOODS';
  const { categories: assetCategories, failed: assetCategoriesFailed } = useAssetCategories(isOpen && isGoodsWork);

  const handleAttachProposals = useCallback(async () => {
    if (!effectiveWrkId || proposalPickerSelected.size === 0) return;
    try {
      const { worksApi } = await import('../../services/wmsService');
      const ids = Array.from(proposalPickerSelected);
      const updated = await worksApi.attachProposals(effectiveWrkId, ids);
      setAttachedProposals(updated || []);
      setShowProposalPicker(false);
      setProposalPickerSelected(new Set());
      setProposalPickerSearch('');
      showSuccess(`Attached ${ids.length} proposal(s)`);
    } catch (e) {
      console.error('Attach proposals error:', e);
      showError('Failed to attach proposals');
    }
  }, [effectiveWrkId, proposalPickerSelected, showSuccess, showError]);

  const handleDetachProposal = useCallback(async (prpslId: number) => {
    if (!effectiveWrkId) return;
    if (!confirm('Detach this proposal from the work?')) return;
    try {
      const { worksApi } = await import('../../services/wmsService');
      await worksApi.detachProposal(effectiveWrkId, prpslId);
      setAttachedProposals((prev) => prev.filter((p) => p.prpsl_id !== prpslId));
      showSuccess('Proposal detached');
    } catch (e) {
      console.error('Detach proposal error:', e);
      showError('Failed to detach proposal');
    }
  }, [effectiveWrkId, showSuccess, showError]);

  // Search proposals for the picker (debounced)
  useEffect(() => {
    if (!showProposalPicker) return;
    if (proposalPickerSearchRef.current) clearTimeout(proposalPickerSearchRef.current);
    proposalPickerSearchRef.current = setTimeout(async () => {
      setProposalPickerLoading(true);
      try {
        // search goes in listParams: buildFilterParams (the first argument) drops it, so
        // the picker used to show the same first proposals whatever was typed.
        const result = await proposalsApi.getAll(
          {},
          1,
          20, // the WMS list endpoints serve at most 20 rows a page
          proposalPickerSearch ? { search: proposalPickerSearch } : {}
        );
        setProposalPickerResults((result.data || []) as AttachedProposal[]);
      } catch (e) {
        console.error('Proposal search failed:', e);
        setProposalPickerResults([]);
      } finally {
        setProposalPickerLoading(false);
      }
    }, 300);
    return () => { if (proposalPickerSearchRef.current) clearTimeout(proposalPickerSearchRef.current); };
  }, [proposalPickerSearch, showProposalPicker]);

  // Load proposal/work data
  const loadProposalData = useCallback(async () => {
    if (!workId) return;

    setLoading(true);
    try {
      // Load work details (not proposal)
      const { worksApi } = await import('../../services/wmsService');
      const data = await worksApi.getById(workId);
      if (data) {
        setWorkDetails(data);
        setProposal({
          prpsl_id: data.workId || 0,
          prpsl_cd: data.workCode || '',
          prpsl_nm: data.workName || '',
          estmt_cst_at: data.sanctionAmount || data.originalEstimate || 0,
          wrk_type_nm: data.type?.name || '',
          schme_nm: data.scheme?.name || '',
          lctn_tx: data.location || '',
          zone_nm: data.ulb || data.zone || '',
          ward_nm: data.wardName || ''
        });
        setEstimateNm(data.workName || '');

        /* Inherit the work's classification onto the new estimate.
         *
         * These four are stored on the estimate in its own right (they can legitimately
         * differ from the work), but they were left blank on every new estimate — the
         * operator had to re-pick values already recorded against the work they had just
         * selected. Only loadEstimateData (edit mode) ever populated them.
         *
         * Existing values are never overwritten: on a re-load, or where the caller has
         * already set a dimension, what is on screen wins. So this fills the blanks and
         * nothing more.
         *
         * UDA is the odd one out — the dropdown is built from the admin-unit master, a
         * different id space from the work's uda_id (VMRDA is uda_id 5 / admn_unt_id 654),
         * so the server resolves it as udaAdmnUntId. */
        /* worksApi.getById is declared as returning WorkSummary, but the endpoint
         * actually returns the fuller work-detail shape (nested type/category/scheme
         * objects, udaAdmnUntId). The surrounding code already reads those fields off
         * it; widening here keeps this block from adding to that mismatch without
         * retyping the shared API signature, which is a larger change than this. */
        const wd = data as any;
        setEstmtWrkTypeId((prev) => prev ?? (wd.type?.id ?? null));
        setEstmtWrkCtgryId((prev) => prev ?? (wd.category?.id ?? null));
        setEstmtSchmeId((prev) => prev ?? (wd.scheme?.id ?? null));
        setEstmtAdmnUntId((prev) => prev ?? (wd.udaAdmnUntId ?? null));

        setOptions([{
          ...initialOption,
          optn_id: uid(),
          optn_nm: 'Option 1',
          lctn_tx: data.location || ''
        }]);
      }
    } catch (error) {
      console.error('Error loading work details:', error);
      showError('Failed to load proposal data');
    } finally {
      setLoading(false);
    }
  }, [workId]);

  // Load existing estimate data
  const loadEstimateData = useCallback(async () => {
    if (!estimateId) return;

    setLoading(true);
    try {
      const [estimateData, itemsData] = await Promise.all([
        estimatesApi.getById(estimateId),
        estimatesApi.getItems(estimateId)
      ]);

      if (estimateData) {
        setEstimateNm(estimateData.estmt_nm || '');
        setEstimateTypeCd(estimateData.estmt_type_cd || 'ORIGINAL');
        setRmrksTx(estimateData.rmrks_tx || '');
        // Dimension fields
        setEstmtAdmnUntId(estimateData.admn_unt_id ?? null);
        setEstmtWrkTypeId(estimateData.wrk_type_id ?? null);
        setEstmtWrkCtgryId(estimateData.wrk_ctgry_id ?? null);
        setEstmtSchmeId(estimateData.schme_id ?? null);

        // Load attached proposals via the work (M:M relation lives at work level)
        if (estimateData.wrk_id) {
          setEstimateWrkId(Number(estimateData.wrk_id));
          loadAttachedProposals(estimateData.wrk_id);
        }

        // Load abstract items
        if (estimateData.abstract_items && estimateData.abstract_items.length > 0) {
          setAbstractItems(estimateData.abstract_items.map((item: any) => ({
            abstrt_itm_id: item.abstrt_itm_id || uid(),
            dscn_tx: item.dscn_tx || '',
            amount_at: item.amount_at || 0
          })));
        }

        // Group items by option
        if (itemsData && itemsData.length > 0) {
          // TODO: Parse options from items data structure
          // For now, put all items in first option
          const items: BOQItem[] = itemsData.map((item: any) => ({
            itm_id: item.itm_id || 0,
            itm_cd: item.itm_cd || '',
            itm_nm: item.itm_nm || '',
            itm_dscn_tx: item.itm_dscn_tx || '',
            itm_type_cd: item.itm_type_cd || 'INDIVIDUAL',
            sor_itm_id: item.sor_itm_id,
            rate_cntrct_id: item.rate_cntrct_id,
            uom_cd: item.uom_cd || '',
            uom_nm: item.uom_nm || '',
            qty_nu: item.qty_nu || 0,
            rate_at: item.rate_at || 0,
            amount_at: (item.qty_nu || 0) * (item.rate_at || 0),
            rmrks_tx: item.rmrks_tx || '',
            // Provenance resolved server-side off the rate-item master, so a reopened
            // estimate shows the SOR item as it stands today rather than a stale copy.
            sor_yr_cd: item.sor_yr_cd || '',
            sor_type_nm: item.sor_type_nm || '',
            sor_rte_at: item.sor_rte_at ?? null,
            asst_ctgry_id: item.asst_ctgry_id ?? null,
            spcfcn_tx: item.spcfcn_tx || ''
          }));

          setOptions([{
            ...initialOption,
            optn_id: uid(),
            items,
            totl_at: items.reduce((sum, itm) => sum + itm.amount_at, 0)
          }]);
        }
      }
    } catch (error) {
      console.error('Error loading estimate:', error);
      showError('Failed to load estimate data');
    } finally {
      setLoading(false);
    }
  }, [estimateId]);

  useEffect(() => {
    if (isOpen) {
      // The form stays mounted between opens on the Estimates page, so a work id left
      // over from a previously edited estimate must be cleared before a fresh open.
      if (!estimateId) setEstimateWrkId(null);
      if (estimateId) {
        loadEstimateData();
      } else if (workId) {
        loadProposalData();
        loadAttachedProposals(workId);
        loadExistingEstimates(workId);
        setAbstractItems([]);
      } else {
        // Pure "new from scratch" — no work yet, no proposals/estimates to load
        setAttachedProposals([]);
        setExistingEstimates([]);
      }
      if (!estimateId && initialEstimateType) {
        setEstimateTypeCd(initialEstimateType);
      }
    }
  }, [isOpen, workId, estimateId, loadProposalData, loadEstimateData, loadAttachedProposals, loadExistingEstimates, initialEstimateType]);

  // Reload existing estimates whenever the bound work changes (after estimate loaded too)
  useEffect(() => {
    if (isOpen && effectiveWrkId) loadExistingEstimates(effectiveWrkId);
  }, [isOpen, effectiveWrkId, loadExistingEstimates]);

  // Add new option
  const addOption = () => {
    const newOption: WorkOption = {
      ...initialOption,
      optn_id: uid(),
      optn_nm: `Option ${options.length + 1}`,
      lctn_tx: proposal?.lctn_tx || ''
    };
    setOptions([...options, newOption]);
    setActiveOptionIdx(options.length);
  };

  // Remove option
  const removeOption = (idx: number) => {
    if (options.length <= 1) {
      showError('At least one option is required');
      return;
    }
    const newOptions = options.filter((_, i) => i !== idx);
    setOptions(newOptions);
    if (activeOptionIdx >= newOptions.length) {
      setActiveOptionIdx(newOptions.length - 1);
    }
  };

  // Update option
  const updateOption = (idx: number, field: keyof WorkOption, value: any) => {
    const newOptions = [...options];
    newOptions[idx] = { ...newOptions[idx], [field]: value };
    setOptions(newOptions);
  };

  // Add BOQ item to current option
  // Handle multi-select from SOR Selector modal
  const handleSORSelect = (sorItems: SORSelectorItem[]) => {
    const newOptions = [...options];
    sorItems.forEach(sor => {
      const newItem: BOQItem = {
        ...initialBOQItem,
        itm_id: uid(),
        itm_cd: sor.sor_itm_cd,
        itm_nm: sor.sor_itm_nm,
        itm_type_cd: 'SOR',
        sor_itm_id: sor.sor_itm_id,
        uom_cd: sor.uom_cd,
        uom_nm: sor.uom_cd,
        rate_at: sor.rate_at,
        qty_nu: 0,
        amount_at: 0,
        // Carried so the line can show its SOR ancestry straight away, without waiting
        // for a save-and-reload to fetch it back off the master.
        sor_yr_cd: sor.sor_yr_cd || '',
        sor_type_nm: sor.sor_type_nm || '',
        sor_rte_at: sor.rate_at
      };
      newOptions[activeOptionIdx].items.push(newItem);
    });
    newOptions[activeOptionIdx].totl_at = newOptions[activeOptionIdx].items.reduce(
      (sum, itm) => sum + itm.amount_at, 0
    );
    setOptions(newOptions);
  };

  // Handle multi-select from Rate Contract Selector
  const handleRCSelect = (rcItems: RateContractItem[]) => {
    const newOptions = [...options];
    rcItems.forEach(rc => {
      const newItem: BOQItem = {
        ...initialBOQItem,
        itm_id: uid(),
        itm_cd: rc.rte_cntrct_cd,
        itm_nm: rc.itm_nm,
        itm_dscn_tx: rc.itm_spcfcn_tx,
        itm_type_cd: 'RATE_CONTRACT',
        rate_cntrct_id: rc.rte_cntrct_itm_id,
        uom_cd: rc.uom_cd,
        uom_nm: rc.uom_nm || rc.uom_cd,
        rate_at: rc.rate_at,
        qty_nu: 0,
        amount_at: 0,
        rmrks_tx: `${rc.rte_cntrct_nm} | ${rc.cntrctr_nm}`
      };
      newOptions[activeOptionIdx].items.push(newItem);
    });
    newOptions[activeOptionIdx].totl_at = newOptions[activeOptionIdx].items.reduce(
      (sum, itm) => sum + itm.amount_at, 0
    );
    setOptions(newOptions);
  };

  const addItemToOption = (item: Partial<BOQItem>) => {
    const newItem: BOQItem = {
      ...initialBOQItem,
      ...item,
      itm_id: uid(),
      amount_at: (item.qty_nu || 0) * (item.rate_at || 0)
    };

    const newOptions = [...options];
    newOptions[activeOptionIdx].items.push(newItem);
    newOptions[activeOptionIdx].totl_at = newOptions[activeOptionIdx].items.reduce(
      (sum, itm) => sum + itm.amount_at, 0
    );
    setOptions(newOptions);
    setShowItemPicker(false);
    setItemSearchTerm('');
  };

  // Update BOQ item
  const updateItem = (optionIdx: number, itemIdx: number, field: keyof BOQItem, value: any) => {
    const newOptions = [...options];
    const item = { ...newOptions[optionIdx].items[itemIdx], [field]: value };

    // Recalculate amount if qty or rate changed
    if (field === 'qty_nu' || field === 'rate_at') {
      item.amount_at = item.qty_nu * item.rate_at;
    }

    newOptions[optionIdx].items[itemIdx] = item;
    newOptions[optionIdx].totl_at = newOptions[optionIdx].items.reduce(
      (sum, itm) => sum + itm.amount_at, 0
    );
    setOptions(newOptions);
  };

  // Remove BOQ item
  const removeItem = (optionIdx: number, itemIdx: number) => {
    const newOptions = [...options];
    newOptions[optionIdx].items = newOptions[optionIdx].items.filter((_, i) => i !== itemIdx);
    newOptions[optionIdx].totl_at = newOptions[optionIdx].items.reduce(
      (sum, itm) => sum + itm.amount_at, 0
    );
    setOptions(newOptions);
  };

  // Abstract Estimate handlers
  const addAbstractItem = () => {
    setAbstractItems([...abstractItems, { abstrt_itm_id: uid(), dscn_tx: '', amount_at: 0 }]);
  };

  const updateAbstractItem = (index: number, field: keyof AbstractEstimateItem, value: any) => {
    const updated = [...abstractItems];
    updated[index] = { ...updated[index], [field]: value };
    setAbstractItems(updated);
  };

  const removeAbstractItem = (index: number) => {
    setAbstractItems(abstractItems.filter((_, i) => i !== index));
  };

  const generateAbstractFromBOQ = () => {
    if (abstractItems.length > 0 && !window.confirm('This will replace existing abstract items. Continue?')) {
      return;
    }
    const generated: AbstractEstimateItem[] = options.map(opt => ({
      abstrt_itm_id: uid(),
      dscn_tx: `${opt.optn_nm}${opt.lctn_tx ? ' - ' + opt.lctn_tx : ''}`,
      amount_at: opt.totl_at
    }));
    setAbstractItems(generated);
  };

  // ── Location design files ────────────────────────────────────────────────────────────
  //
  // These used to be collected here and thrown away: the picked File sat in component
  // state, the save payload carried only { file_nm, file_url: null }, and POST/PUT
  // /estimates ignores the field outright — while the Status badge was hardcoded to
  // "Pending", so the loss never looked like a failure. They are now uploaded for real, on
  // save, to the same store the Designs screen reads (dswms_drwng_lst_t, keyed by wrk_id),
  // tagged with the option name so a location design stays distinguishable from the
  // work-level design set.
  //
  // getDrawings()/uploadDrawing() are given the WORK id, not a design id: designs are
  // grouped by work, and getById() returns a shell for a work with no drawings yet, so the
  // first upload for an undesigned work succeeds. WorkProfile calls them the same way.
  const [workDrawings, setWorkDrawings] = useState<WorkDrawing[]>([]);
  const [loadingDrawings, setLoadingDrawings] = useState(false);

  const loadWorkDrawings = useCallback(async () => {
    if (!effectiveWrkId) {
      setWorkDrawings([]);
      return;
    }
    setLoadingDrawings(true);
    try {
      const { designsApi } = await import('../../services/wmsService');
      const rows = await designsApi.getDrawings(effectiveWrkId);
      setWorkDrawings(Array.isArray(rows) ? rows : []);
    } catch (e) {
      // A work with no design yet is a normal state, not an error worth a toast — the
      // panel renders its own empty state.
      console.error('Error loading design files for work:', e);
      setWorkDrawings([]);
    } finally {
      setLoadingDrawings(false);
    }
  }, [effectiveWrkId]);

  useEffect(() => {
    if (!isOpen) return;
    loadWorkDrawings();
  }, [isOpen, loadWorkDrawings]);

  // Files already stored for a given option. Uploaded files are never mirrored into option
  // state — they are always read back from the work's drawings, so there is one source of
  // truth and a reopened estimate shows exactly what the Designs screen shows.
  const uploadedFilesForOption = useCallback(
    (optionName: string) => workDrawings.filter(d => (d.dscn_tx || '').startsWith(LOCATION_TAG(optionName))),
    [workDrawings]
  );

  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const locationFileInputRef = useRef<HTMLInputElement>(null);

  const addLocationFiles = (optionIdx: number, files: FileList | null, forcedType?: string) => {
    if (!files || files.length === 0) return;
    const queued: LocationDesignFile[] = Array.from(files).map(f => ({
      file_id: uid(),
      file_nm: f.name,
      file_obj: f,
      file_type_cd: forcedType || drawingType(null, f.name),
      file_sz: f.size,
      uploading: false
    }));
    setOptions(prev => prev.map((opt, i) =>
      i === optionIdx ? { ...opt, dsgn_files: [...opt.dsgn_files, ...queued] } : opt
    ));
  };

  const removeQueuedFile = (optionIdx: number, fileId: number) => {
    setOptions(prev => prev.map((opt, i) =>
      i === optionIdx ? { ...opt, dsgn_files: opt.dsgn_files.filter(f => f.file_id !== fileId) } : opt
    ));
  };

  const removeUploadedFile = async (drwngId: number) => {
    if (!effectiveWrkId) return;
    if (!confirm('Delete this design file? It is removed from the work design as well.')) return;
    try {
      const { designsApi } = await import('../../services/wmsService');
      await designsApi.deleteDrawing(effectiveWrkId, drwngId);
      await loadWorkDrawings();
      showSuccess('Design file deleted');
    } catch (e: any) {
      console.error('Delete drawing error:', e);
      showError(e.response?.data?.message || 'Failed to delete design file');
    }
  };

  /**
   * Upload every queued file, tagging each with its option so the location it belongs to
   * survives in the Designs screen. Returns the names that failed; the caller reports them
   * rather than silently succeeding — the whole point of this rewrite.
   */
  const uploadQueuedFiles = async (wrkId: number): Promise<string[]> => {
    const failed: string[] = [];
    const { designsApi } = await import('../../services/wmsService');

    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      for (const queued of opt.dsgn_files) {
        setOptions(prev => prev.map((o, oi) => oi !== i ? o : {
          ...o,
          dsgn_files: o.dsgn_files.map(f => f.file_id === queued.file_id ? { ...f, uploading: true, error: undefined } : f)
        }));

        const formData = new FormData();
        // Field name must be 'files' — the global multer middleware populates req.files.
        formData.append('files', queued.file_obj);
        formData.append('drwng_nm', queued.file_nm);
        formData.append('drwng_type_cd', queued.file_type_cd);
        formData.append('dscn_tx', `${LOCATION_TAG(opt.optn_nm)}${opt.lctn_tx ? ` — ${opt.lctn_tx}` : ''}`);

        try {
          await designsApi.uploadDrawing(wrkId, formData);
          // Drop it from the queue; it now belongs to the work's drawings.
          setOptions(prev => prev.map((o, oi) => oi !== i ? o : {
            ...o,
            dsgn_files: o.dsgn_files.filter(f => f.file_id !== queued.file_id)
          }));
        } catch (e) {
          console.error('Upload location design file failed:', queued.file_nm, e);
          failed.push(queued.file_nm);
          setOptions(prev => prev.map((o, oi) => oi !== i ? o : {
            ...o,
            dsgn_files: o.dsgn_files.map(f => f.file_id === queued.file_id ? { ...f, uploading: false, error: 'Upload failed' } : f)
          }));
        }
      }
    }
    return failed;
  };

  // Add item from SOR
  const addSORItem = (sorItem: SORItem) => {
    addItemToOption({
      itm_cd: sorItem.sor_itm_cd,
      itm_nm: sorItem.sor_itm_nm,
      itm_dscn_tx: sorItem.sor_itm_dscn_tx,
      itm_type_cd: 'SOR',
      sor_itm_id: sorItem.sor_itm_id,
      uom_cd: sorItem.uom_cd,
      uom_nm: sorItem.uom_nm,
      rate_at: sorItem.rate_at,
      qty_nu: 1
    });
  };

  // Filter SOR items by search
  const filteredSORItems = sorItems.filter(item =>
    item.sor_itm_nm?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
    item.sor_itm_cd?.toLowerCase().includes(itemSearchTerm.toLowerCase())
  );

  // Filter Rate Contract items by search
  const filteredRCItems = rateContractItems.filter(item =>
    item.rate_cntrct_nm?.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
    item.rate_cntrct_cd?.toLowerCase().includes(itemSearchTerm.toLowerCase())
  );

  // Add single Rate Contract item from inline picker
  const addRCItem = (rcItem: RateContractItem) => {
    addItemToOption({
      itm_cd: rcItem.rate_cntrct_cd,
      itm_nm: rcItem.rate_cntrct_nm,
      itm_dscn_tx: rcItem.rate_cntrct_nm,
      itm_type_cd: 'RATE_CONTRACT',
      rate_cntrct_id: rcItem.rate_cntrct_id,
      uom_cd: rcItem.uom_cd,
      uom_nm: rcItem.uom_cd,
      rate_at: rcItem.rate_at,
      qty_nu: 1
    });
  };

  // Calculate grand total
  // BOQ is the primary source of the estimate value; the abstract stands in when no BOQ
  // lines were entered. Without the fallback an estimate built purely from the Abstract
  // Estimate section saved with totl_at = 0 — the typed amount was computed into
  // abstractTotal and sent as abstrt_totl_at, but totl_at (the column the header stores)
  // summed only the BOQ options, so the Estimates list and profile both showed zero.
  const grandTotal = options.reduce((sum, opt) => sum + opt.totl_at, 0) || abstractTotal;

  // Save estimate
  const handleSave = async () => {
    // NEVER fall back to proposal.prpsl_id here. `proposal` is seeded from the work's
    // ATTACHED PROPOSALS (loadAttachedProposals), and proposal ids and work ids are
    // different id spaces — proposals run 1..37, works start at 601. Editing an estimate
    // from the Estimates list left workDetails null, so the save sent wrk_id = prpsl_id
    // and Postgres rejected it with dswms_estmt_lst_t_wrk_id_fkey (23503). The estimate
    // already knows its work, so read it off the estimate instead.
    const resolvedWorkId = workDetails?.workId || estimateWrkId || workId;
    if (!resolvedWorkId) {
      showError('No work selected');
      return;
    }

    if (blockOriginalDuplicate) {
      showError('An ORIGINAL estimate already exists for this work. Switch to Revised or Supplementary, or edit the existing one.');
      return;
    }

    if (options.every(opt => opt.items.length === 0) && abstractItems.length === 0) {
      showError('Please add at least one BOQ item or abstract estimate item');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        wrk_id: resolvedWorkId,
        estmt_nm: estimateNm,
        estmt_type_cd: estimateTypeCd,
        totl_at: grandTotal,
        rmrks_tx: rmrksTx,
        admn_unt_id: estmtAdmnUntId,
        wrk_type_id: estmtWrkTypeId,
        wrk_ctgry_id: estmtWrkCtgryId,
        schme_id: estmtSchmeId,
        abstract_items: abstractItems.map(item => ({
          dscn_tx: item.dscn_tx,
          amount_at: item.amount_at
        })),
        abstrt_totl_at: abstractTotal,
        // dsgn_sts_cd / dsgn_files are deliberately absent: the estimate endpoints never
        // read them, and design status belongs to the design record, not to a BOQ option.
        options: options.map(opt => ({
          optn_nm: opt.optn_nm,
          lctn_tx: opt.lctn_tx,
          totl_at: opt.totl_at,
          items: opt.items.map(item => ({
            itm_cd: item.itm_cd,
            itm_nm: item.itm_nm,
            itm_dscn_tx: item.itm_dscn_tx,
            itm_type_cd: item.itm_type_cd,
            sor_itm_id: item.sor_itm_id,
            rate_cntrct_id: item.rate_cntrct_id,
            uom_cd: item.uom_cd,
            qty_nu: item.qty_nu,
            rate_at: item.rate_at,
            amount_at: item.amount_at,
            rmrks_tx: item.rmrks_tx,
            // Only a GOODS work may carry asset categories; the server refuses them otherwise.
            asst_ctgry_id: isGoodsWork ? (item.asst_ctgry_id ?? null) : null,
            spcfcn_tx: item.spcfcn_tx || null
          }))
        }))
      };

      if (isEdit && estimateId) {
        await estimatesApi.update(estimateId, payload);
        showSuccess('Estimate updated successfully');
      } else {
        await estimatesApi.create(payload);
        showSuccess('Estimate created successfully');
      }

      // Upload the location design files queued against each option. This runs after the
      // estimate is stored so a failed upload never costs the user their BOQ — and unlike
      // the old behaviour, a failure is reported instead of being swallowed.
      const queuedCount = options.reduce((n, o) => n + o.dsgn_files.length, 0);
      if (queuedCount > 0) {
        const uploadWrkId = effectiveWrkId ?? (typeof resolvedWorkId === 'number' ? resolvedWorkId : null);
        if (!uploadWrkId) {
          showError(`Estimate saved, but ${queuedCount} design file(s) could not be uploaded: no work is linked yet. Link the work, then re-add them.`);
          setSaving(false);
          return;
        }
        const failed = await uploadQueuedFiles(uploadWrkId);
        await loadWorkDrawings();
        if (failed.length > 0) {
          // Keep the form open so the failed rows stay visible and can be retried.
          showError(`Estimate saved, but ${failed.length} design file(s) failed to upload: ${failed.join(', ')}`);
          setSaving(false);
          return;
        }
        showSuccess(`${queuedCount} design file(s) uploaded`);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving estimate:', error);
      showError(error.response?.data?.message || 'Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const activeOption = options[activeOptionIdx];

  // Design state for the active location. Derived from what is actually stored, never from
  // a field the user sets — the old editable "Design Status" dropdown was never persisted,
  // so it always read "Design Pending" again on reopen.
  const activeLocationFiles = uploadedFilesForOption(activeOption.optn_nm);
  const activeLocationFileCount = activeLocationFiles.length + activeOption.dsgn_files.length;

  return createPortal(
    <div className="fixed inset-0 overflow-y-auto z-[9999]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>

      {/* Modal */}
      <div className={`flex min-h-full items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}>
        <div className={`relative bg-white shadow-xl transform transition-all ${
          isFullscreen
            ? 'w-full h-screen rounded-none'
            : 'w-full max-w-6xl rounded-xl'
        }`}>
          {/* Header */}
          <div className={`${WMS_POPUP_HEADER} ${
            isFullscreen ? '' : 'rounded-t-xl'
          }`}>
            <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className={WMS_POPUP_ICON}>
                  <i className="ri-file-list-3-line"></i>
                </div>
                <div className="min-w-0">
                  <h2 className={WMS_POPUP_TITLE}>
                    {isEdit ? 'Edit Estimate' : 'New Work Estimate'}
                  </h2>
                  <p className={WMS_POPUP_SUBTITLE}>
                    {proposal ? `${proposal.prpsl_cd} - ${proposal.prpsl_nm}` : 'Create estimate with BOQ items'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* The authority this estimate is filed into. The page scope strip is
                    behind this dialog, so it is repeated here: the server refuses the
                    save while the scope is "All UDAs". */}
                <WmsUdaSelect tone="dark" />
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className={WMS_POPUP_CLOSE}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
                </button>
                <button
                  onClick={onClose}
                  className={WMS_POPUP_CLOSE}
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`flex ${isFullscreen ? 'h-[calc(100vh-130px)]' : 'max-h-[70vh]'}`}>
            {/* Left Panel - Options List (only shown when multiple options exist) */}
            {options.length > 1 && (
              <div className="w-64 bg-gray-100 border-r flex flex-col">
                <div className="p-3 border-b bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Options / Locations</span>
                    <button
                      onClick={addOption}
                      className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                      title="Add Alternative Option"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {options.map((opt, idx) => (
                    <div
                      key={opt.optn_id}
                      onClick={() => setActiveOptionIdx(idx)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        activeOptionIdx === idx
                          ? 'bg-emerald-100 border-2 border-emerald-500'
                          : 'bg-white border border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{opt.optn_nm}</span>
                        {options.length > 1 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeOption(idx);
                            }}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate mb-2">
                        {opt.lctn_tx || 'No location specified'}
                      </div>
                      {/* Derived from stored files, not from a user-set field. The badge here
                          used to read opt.dsgn_sts_cd, which was never saved — so it showed
                          "Design Pending" on every reopen however many files had been attached. */}
                      <div className="flex items-center justify-between text-xs">
                        {(() => {
                          const stored = uploadedFilesForOption(opt.optn_nm).length;
                          const queued = opt.dsgn_files.length;
                          return (
                            <span className={`px-1.5 py-0.5 rounded ${
                              stored > 0 ? 'bg-green-100 text-green-700' :
                              queued > 0 ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {stored > 0 ? `Design Uploaded (${stored})` :
                               queued > 0 ? `${queued} pending save` : 'Design Pending'}
                            </span>
                          );
                        })()}
                        <span className="font-medium text-emerald-700">
                          {opt.items.length} items
                        </span>
                      </div>
                      <div className="mt-2 text-right">
                        <span className="text-sm font-bold text-gray-700">
                          ₹{opt.totl_at.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Grand Total */}
                <div className="p-3 border-t bg-emerald-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Grand Total</span>
                    <span className="text-lg font-bold text-emerald-700">
                      ₹{grandTotal.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Right Panel - Option Details & BOQ */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {loading ? (
                <ListSkeleton />
              ) : (
                <div className="space-y-6">
                  {/* Work Details */}
                  {(workDetails || proposal) && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-4">
                      {/* Work header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-building-4-line text-blue-600 text-lg"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-bold text-blue-700">{workDetails?.workCode || proposal?.prpsl_cd}</span>
                            {workDetails?.status?.name && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">{workDetails.status.name}</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-800 truncate">{workDetails?.workName || proposal?.prpsl_nm}</p>
                        </div>
                      </div>
                      {/* Work details grid */}
                      <div className="grid grid-cols-5 gap-3 text-sm">
                        <div>
                          <span className="text-[10px] text-gray-500 block">Work Type/Sector</span>
                          <p className="font-medium text-xs">{workDetails?.type?.name || proposal?.wrk_type_nm || '-'}</p>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-500 block">Category</span>
                          <p className="font-medium text-xs">{workDetails?.category?.name || '-'}</p>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-500 block">UDA</span>
                          <p className="font-medium text-xs">{workDetails?.ulb || workDetails?.zone || proposal?.zone_nm || '-'}</p>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-500 block">Scheme</span>
                          <p className="font-medium text-xs">{workDetails?.scheme?.name || proposal?.schme_nm || '-'}</p>
                        </div>
                        <div>
                          <span className="text-[10px] text-gray-500 block">Sanctioned Amount</span>
                          <p className="font-medium text-xs text-emerald-700">₹{(workDetails?.sanctionAmount || proposal?.estmt_cst_at || 0).toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Attached Proposals */}
                  <div className="bg-purple-50/40 p-4 rounded-lg border border-purple-200">
                    <SectionHeader
                      icon="ri-stack-line"
                      title="Attached Proposals"
                      action={
                        <button
                          type="button"
                          onClick={() => { setProposalPickerSelected(new Set()); setProposalPickerSearch(''); setShowProposalPicker(true); }}
                          disabled={!effectiveWrkId}
                          title={!effectiveWrkId ? 'Pick or save a work first to attach proposals' : 'Attach a proposal'}
                          className="px-2 py-1 bg-white/20 hover:bg-white/30 text-white text-xs rounded-md flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <i className="ri-add-line"></i> Attach Proposal
                        </button>
                      }
                    />
                    {loadingProposals ? (
                      <ListSkeleton rows={2} avatar={false} />
                    ) : attachedProposals.length === 0 ? (
                      <div className="text-sm text-gray-500 italic px-4 py-3 bg-white rounded border border-dashed text-center">
                        No proposal attached
                        {effectiveWrkId && (
                          <span className="block text-xs text-gray-400 mt-1">Use "Attach Proposal" to link one or more proposals to this work.</span>
                        )}
                      </div>
                    ) : (
                      <ul className="bg-white rounded-lg border divide-y">
                        {attachedProposals.map((p) => (
                          <li key={p.prpsl_id} className="flex items-center justify-between gap-3 px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{p.prpsl_cd}</span>
                                <span className="text-sm font-medium text-gray-900 truncate">{p.prpsl_nm}</span>
                                {p.prpsl_sts_cd && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.prpsl_sts_cd}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                                <span>Estimated: <span className="font-medium text-emerald-700">₹{(p.estmt_cst_at || 0).toLocaleString('en-IN')}</span></span>
                                {p.lctn_tx && <span className="truncate">{p.lctn_tx}</span>}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleDetachProposal(p.prpsl_id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title="Detach proposal"
                            >
                              <i className="ri-close-circle-line text-base"></i>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Existing Estimates on This Work — duplicate guard */}
                  {!isEdit && existingEstimates.length > 0 && (
                    <div className={`p-4 rounded-lg border ${blockOriginalDuplicate ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
                      <div className="flex items-start gap-3 mb-3">
                        <i className={`ri-${blockOriginalDuplicate ? 'error-warning' : 'information'}-line text-xl ${blockOriginalDuplicate ? 'text-red-600' : 'text-amber-600'} mt-0.5`}></i>
                        <div className="flex-1">
                          <h4 className={`text-sm font-semibold ${blockOriginalDuplicate ? 'text-red-800' : 'text-amber-800'}`}>
                            {blockOriginalDuplicate
                              ? 'An ORIGINAL estimate already exists for this work'
                              : `This work already has ${existingEstimates.length} estimate${existingEstimates.length > 1 ? 's' : ''}`}
                          </h4>
                          <p className={`text-xs mt-0.5 ${blockOriginalDuplicate ? 'text-red-700' : 'text-amber-700'}`}>
                            {blockOriginalDuplicate
                              ? 'Switch to Revised or Supplementary, or close this and open the existing estimate to edit.'
                              : 'Review the existing entries below before creating a new one. Use Revised/Supplementary for re-estimates.'}
                          </p>
                        </div>
                      </div>
                      <ul className="bg-white rounded-lg border divide-y">
                        {existingEstimates.map((e) => {
                          const colors: Record<string, string> = {
                            ORIGINAL: 'bg-blue-100 text-blue-700',
                            REVISED: 'bg-amber-100 text-amber-700',
                            SUPPLEMENTARY: 'bg-purple-100 text-purple-700'
                          };
                          return (
                            <li key={e.estmt_id} className="flex items-center justify-between gap-3 px-3 py-2">
                              <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">{e.estmt_cd}</span>
                                <span className={`px-2 py-0.5 rounded text-xs ${colors[e.estmt_type_cd] || 'bg-gray-100'}`}>{e.estmt_type_cd}</span>
                                <span className="text-sm text-gray-800 truncate">{e.estmt_nm}</span>
                                <span className="text-xs text-gray-500 ml-auto">₹{(e.totl_at || 0).toLocaleString('en-IN')}</span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Estimate Details */}
                  <div className="bg-emerald-50/50 p-4 rounded-lg border border-emerald-200">
                    <SectionHeader icon="ri-file-text-line" title="Estimate Details" />
                    <div className="grid grid-cols-3 gap-4 bg-white p-4 rounded-lg border">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Estimate Name
                        </label>
                        <input
                          type="text"
                          value={estimateNm}
                          onChange={(e) => setEstimateNm(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="Enter estimate name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Estimate Type
                        </label>
                        <select
                          value={estimateTypeCd}
                          onChange={(e) => setEstimateTypeCd(e.target.value as any)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          {estimateTypes.map(t => <option key={t.estmt_typ_id} value={t.estmt_typ_cd}>{t.estmt_typ_nm}</option>)}
                        </select>
                      </div>
                      {/* Dimension fields (optional) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Work Type/Sector
                        </label>
                        <select
                          value={estmtWrkTypeId ?? ''}
                          onChange={(e) => setEstmtWrkTypeId(e.target.value === '' ? null : parseInt(e.target.value))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="">Select Type</option>
                          {workTypes.map((t: any) => <option key={t.wrk_type_id} value={t.wrk_type_id}>{t.wrk_type_nm}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Work Category
                        </label>
                        <select
                          value={estmtWrkCtgryId ?? ''}
                          onChange={(e) => setEstmtWrkCtgryId(e.target.value === '' ? null : parseInt(e.target.value))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="">Select Category</option>
                          {workCategories.map((c: any) => <option key={c.wrk_ctgry_id} value={c.wrk_ctgry_id}>{c.wrk_ctgry_nm}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Scheme <span className="text-xs text-gray-400 font-normal">(optional)</span>
                        </label>
                        <select
                          value={estmtSchmeId ?? ''}
                          onChange={(e) => setEstmtSchmeId(e.target.value === '' ? null : parseInt(e.target.value))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="">Select Scheme</option>
                          {schemes.map((s: any) => <option key={s.schme_id} value={s.schme_id}>{s.schme_nm}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          UDA <span className="text-xs text-gray-400 font-normal">(optional)</span>
                        </label>
                        <select
                          value={estmtAdmnUntId ?? ''}
                          onChange={(e) => setEstmtAdmnUntId(e.target.value === '' ? null : parseInt(e.target.value))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        >
                          <option value="">Select UDA</option>
                          {ulbsList.map((u) => <option key={u.id} value={u.id}>{u.nm}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Add Alternative Option button (single option mode) */}
                  {options.length === 1 && (
                    <div className="flex items-center justify-between px-3 py-2 bg-white border border-dashed border-gray-300 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <i className="ri-information-line text-blue-400"></i>
                        <span>Single option estimate. Need to compare alternatives?</span>
                      </div>
                      <button
                        type="button"
                        onClick={addOption}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors"
                      >
                        <i className="ri-add-circle-line"></i>
                        Add Alternative Option
                      </button>
                    </div>
                  )}

                  {/* Active Option Details */}
                  <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-200">
                    <SectionHeader
                      icon="ri-map-pin-line"
                      title={`${activeOption.optn_nm} - Location & Design`}
                    />
                    <div className="bg-white p-4 rounded-lg border space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Option Name
                          </label>
                          <input
                            type="text"
                            value={activeOption.optn_nm}
                            onChange={(e) => updateOption(activeOptionIdx, 'optn_nm', e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Option 1, Location A"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Design Status
                          </label>
                          {/* Read-only and derived. This was an editable dropdown whose value
                              no endpoint stored, so it reset to "Design Pending" on every
                              reopen no matter what had been attached. */}
                          <div
                            className={`w-full border rounded-lg px-3 py-2 flex items-center gap-2 ${
                              activeLocationFiles.length > 0
                                ? 'bg-green-50 border-green-200 text-green-800'
                                : 'bg-gray-50 border-gray-200 text-gray-600'
                            }`}
                            title="Derived from the design files attached to this location"
                          >
                            <i className={activeLocationFiles.length > 0 ? 'ri-check-line' : 'ri-time-line'}></i>
                            {activeLocationFiles.length > 0 ? 'Design Uploaded' : 'Design Pending'}
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location Details
                        </label>
                        <textarea
                          value={activeOption.lctn_tx}
                          onChange={(e) => updateOption(activeOptionIdx, 'lctn_tx', e.target.value)}
                          rows={2}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter location details..."
                        />
                      </div>

                      {/* ── Location Design Files ──────────────────────────────────────
                          Scoped to THIS option/location, which is what separates it from the
                          Designs screen's work-level "Work Design" set. Both end up in the
                          same store (there is no per-option drawing table); the option name is
                          written into each file's description so the two stay distinguishable
                          and the origin is readable from the Designs screen. */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Location Design Files ({activeLocationFileCount})
                          </label>
                          <a
                            href="/app/ds-wms/designs"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                            title="Work-level design set — opens in a new tab so this estimate is not lost"
                          >
                            <i className="ri-external-link-line"></i>
                            Work Design
                          </a>
                        </div>
                        <p className="text-xs text-gray-500 mb-3 flex items-start gap-1">
                          <i className="ri-information-line mt-0.5"></i>
                          <span>
                            Files for <strong>{activeOption.optn_nm}</strong> only. They upload when you
                            save the estimate, and are tagged with this location. The whole-work drawing
                            set lives in <strong>Works &gt; Designs</strong>.
                          </span>
                        </p>

                        {/* Upload buttons by type */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {([
                            { type: 'BIM', label: 'BIM Files', icon: 'ri-building-3-line', color: 'blue', accept: '.ifc,.rvt,.nwd,.nwc,.nwf,.dgn' },
                            { type: 'CAD', label: 'CAD Files', icon: 'ri-pencil-ruler-2-line', color: 'purple', accept: '.dwg,.dxf,.dwf,.dws' },
                            { type: 'GIS', label: 'GIS Files', icon: 'ri-map-2-line', color: 'teal', accept: '.kml,.kmz,.shp,.shx,.dbf,.prj,.geojson,.gpx' },
                            { type: 'PDF', label: 'PDF Documents', icon: 'ri-file-pdf-line', color: 'red', accept: '.pdf' },
                            { type: 'IMAGE', label: 'Images', icon: 'ri-image-line', color: 'green', accept: '.jpg,.jpeg,.png,.gif,.tiff,.bmp' },
                            { type: 'OTHER', label: 'Other', icon: 'ri-file-line', color: 'gray', accept: '.doc,.docx,.xls,.xlsx,.csv,.zip' },
                          ] as const).map(ft => (
                            <button key={ft.type} type="button"
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.multiple = true;
                                input.accept = ft.accept;
                                input.onchange = (e) => addLocationFiles(activeOptionIdx, (e.target as HTMLInputElement).files, ft.type);
                                input.click();
                              }}
                              className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors
                                ${ft.color === 'blue' ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' :
                                  ft.color === 'purple' ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' :
                                  ft.color === 'teal' ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' :
                                  ft.color === 'red' ? 'bg-red-50 text-red-700 hover:bg-red-100' :
                                  ft.color === 'green' ? 'bg-green-50 text-green-700 hover:bg-green-100' :
                                  'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}>
                              <i className={ft.icon}></i> {ft.label}
                            </button>
                          ))}
                        </div>

                        {/* Drag & Drop Zone */}
                        <div
                          onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                          onDragLeave={(e) => { e.preventDefault(); setIsDraggingFile(false); }}
                          onDrop={(e) => {
                            e.preventDefault();
                            setIsDraggingFile(false);
                            addLocationFiles(activeOptionIdx, e.dataTransfer.files);
                          }}
                          onClick={() => locationFileInputRef.current?.click()}
                          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all mb-3 ${
                            isDraggingFile ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                          }`}
                        >
                          <input ref={locationFileInputRef} type="file" multiple
                            accept=".ifc,.rvt,.nwd,.nwc,.nwf,.dgn,.dwg,.dxf,.dwf,.dws,.kml,.kmz,.shp,.shx,.dbf,.prj,.geojson,.gpx,.pdf,.jpg,.jpeg,.png,.gif,.tiff,.bmp,.doc,.docx,.xls,.xlsx,.csv,.zip"
                            onChange={(e) => { addLocationFiles(activeOptionIdx, e.target.files); e.target.value = ''; }}
                            className="hidden" />
                          <i className={`ri-upload-cloud-2-line text-3xl mb-2 block ${isDraggingFile ? 'text-indigo-500' : 'text-gray-400'}`}></i>
                          <p className="text-gray-600 text-sm mb-1">
                            <span className="font-medium text-indigo-600">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-400">
                            BIM (.ifc, .rvt) | CAD (.dwg, .dxf) | GIS (.kml, .shp) | PDF | Images | Documents
                          </p>
                        </div>

                        {/* Files Table — stored files first, then anything queued for upload */}
                        {activeLocationFileCount > 0 && (
                          <div className="border rounded-lg overflow-hidden">
                            <table data-wms-table className="w-full text-sm">
                              <thead className="bg-gray-50 border-b">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-8">#</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">File Name</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-24">Type</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 w-28">Status</th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 w-20">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {activeLocationFiles.map((d, idx) => {
                                  const type = drawingType(d.drwng_type_cd, d.file_nm || d.drwng_nm);
                                  const style = DRAWING_TYPE_STYLES[type];
                                  return (
                                    <tr key={`stored-${d.drwng_id}`} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                                      <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                          <i className={style.icon}></i>
                                          <span className="font-medium text-xs truncate max-w-[250px]" title={d.file_nm || ''}>
                                            {d.drwng_nm || d.file_nm}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${style.chip}`}>{d.drwng_type_nm || type}</span>
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center gap-1 w-fit">
                                          <i className="ri-check-line"></i> Uploaded
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          {d.file_url && (
                                            <a href={d.file_url} target="_blank" rel="noopener noreferrer"
                                              className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Open file">
                                              <i className="ri-download-line text-sm"></i>
                                            </a>
                                          )}
                                          <button type="button" onClick={() => removeUploadedFile(d.drwng_id)}
                                            className="p-1 text-red-500 hover:bg-red-50 rounded" title="Delete">
                                            <i className="ri-delete-bin-line text-sm"></i>
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                                {activeOption.dsgn_files.map((file, idx) => {
                                  const style = DRAWING_TYPE_STYLES[file.file_type_cd] || DRAWING_TYPE_STYLES.OTHER;
                                  const sizeStr = file.file_sz > 1048576
                                    ? `${(file.file_sz / 1048576).toFixed(1)} MB`
                                    : `${(file.file_sz / 1024).toFixed(0)} KB`;
                                  return (
                                    <tr key={`queued-${file.file_id}`} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 text-gray-500">{activeLocationFiles.length + idx + 1}</td>
                                      <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                          <i className={style.icon}></i>
                                          <span className="font-medium text-xs truncate max-w-[250px]" title={file.file_nm}>{file.file_nm}</span>
                                          <span className="text-[10px] text-gray-400">{sizeStr}</span>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${style.chip}`}>{file.file_type_cd}</span>
                                      </td>
                                      <td className="px-3 py-2">
                                        {file.uploading ? (
                                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1 w-fit">
                                            <i className="ri-loader-4-line animate-spin"></i> Uploading
                                          </span>
                                        ) : file.error ? (
                                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full flex items-center gap-1 w-fit" title={file.error}>
                                            <i className="ri-error-warning-line"></i> Failed
                                          </span>
                                        ) : (
                                          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1 w-fit"
                                            title="Uploads when you save the estimate">
                                            <i className="ri-time-line"></i> Pending save
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <button type="button" onClick={() => removeQueuedFile(activeOptionIdx, file.file_id)}
                                          className="p-1 text-red-500 hover:bg-red-50 rounded" title="Remove">
                                          <i className="ri-delete-bin-line text-sm"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            {loadingDrawings && (
                              <div className="bg-gray-50 px-3 py-1.5 border-t text-xs text-gray-500">
                                <i className="ri-loader-4-line animate-spin mr-1"></i>Refreshing stored files...
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* BOQ Items */}
                  <div className="bg-amber-50/50 p-4 rounded-lg border border-amber-200">
                    <SectionHeader
                      icon="ri-list-check-2"
                      title="Bill of Quantities (BOQ)"
                      action={
                        <button
                          onClick={() => setShowItemPicker(true)}
                          className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm flex items-center gap-1 transition-colors"
                        >
                          <i className="ri-add-line"></i>
                          Add Item
                        </button>
                      }
                    />

                    {/* Item Picker Modal */}
                    {showItemPicker && (
                      <div className="mb-4 bg-white rounded-lg border shadow-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex gap-1">
                            <button onClick={() => setItemPickerType('SOR')}
                              className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${itemPickerType === 'SOR' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                              <i className="ri-file-list-3-line text-xs"></i> From SOR
                            </button>
                            {hasRateContracts && (
                              <button onClick={() => setItemPickerType('RATE_CONTRACT')}
                                className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${itemPickerType === 'RATE_CONTRACT' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                <i className="ri-price-tag-3-line text-xs"></i> From Rate Contract
                              </button>
                            )}
                            <button onClick={() => setItemPickerType('INDIVIDUAL')}
                              className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${itemPickerType === 'INDIVIDUAL' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                              <i className="ri-add-circle-line text-xs"></i> Individual Item
                            </button>
                            <button onClick={() => { setItemPickerType('FROM_WORK'); setImportSelectedWork(null); setImportWorkItems([]); setImportWorkSearch(''); }}
                              className={`px-3 py-1.5 rounded-lg text-sm transition-colors flex items-center gap-1 ${itemPickerType === 'FROM_WORK' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                              <i className="ri-file-copy-line text-xs"></i> From Existing Work
                            </button>
                          </div>
                          <button onClick={() => { setShowItemPicker(false); setItemSearchTerm(''); }}
                            className="p-1 text-gray-500 hover:text-gray-700">
                            <i className="ri-close-line"></i>
                          </button>
                        </div>

                        {itemPickerType === 'FROM_WORK' ? (
                          <div className="space-y-3">
                            {!importSelectedWork ? (
                              <>
                                <div className="relative">
                                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                                  <input type="text" value={importWorkSearch} onChange={(e) => setImportWorkSearch(e.target.value)}
                                    placeholder="Search work by code, name, UDA..."
                                    className="w-full pl-10 pr-4 border rounded-lg px-3 py-2 text-sm" autoFocus />
                                </div>
                                <div className="max-h-48 overflow-y-auto border rounded-lg">
                                  {importWorkLoading ? (
                                    <div className="p-3"><ListSkeleton rows={3} avatar={false} /></div>
                                  ) : importWorkResults.length > 0 ? (
                                    importWorkResults.map((w: any) => (
                                      <button key={w.wrk_id} type="button" onClick={() => selectImportWork(w)}
                                        className="w-full px-3 py-2 text-left hover:bg-amber-50 border-b border-gray-50 flex items-center justify-between">
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs font-semibold text-amber-700">{w.wrk_cd}</span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{w.wrk_sts_nm}</span>
                                          </div>
                                          <p className="text-sm text-gray-800 truncate">{w.wrk_nm}</p>
                                        </div>
                                        <i className="ri-arrow-right-s-line text-gray-300 ml-2"></i>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="p-4 text-center text-gray-400 text-sm">
                                      {importWorkSearch ? 'No works found' : 'Type to search for a work'}
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Selected work header */}
                                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                  <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <i className="ri-building-4-line text-amber-600"></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-mono text-xs font-semibold text-amber-700">{importSelectedWork.wrk_cd}</span>
                                    <p className="text-sm text-gray-800 truncate">{importSelectedWork.wrk_nm}</p>
                                  </div>
                                  <button onClick={() => { setImportSelectedWork(null); setImportWorkItems([]); }}
                                    className="p-1 text-gray-400 hover:text-gray-600"><i className="ri-close-circle-line text-lg"></i></button>
                                </div>

                                {/* Items preview */}
                                {importItemsLoading ? (
                                  <TableSkeleton rows={4} showToolbar={false} />
                                ) : importWorkItems.length === 0 ? (
                                  <div className="p-4 text-center text-gray-400 text-sm">No BOQ items found for this work</div>
                                ) : (
                                  <>
                                    <div className="max-h-48 overflow-y-auto border rounded-lg">
                                      <table data-wms-table className="w-full text-xs">
                                        <thead className="bg-gray-50 sticky top-0">
                                          <tr>
                                            <th className="px-2 py-1.5 text-left">#</th>
                                            <th className="px-2 py-1.5 text-left">Code</th>
                                            <th className="px-2 py-1.5 text-left">Description</th>
                                            <th className="px-2 py-1.5 text-center">UOM</th>
                                            <th className="px-2 py-1.5 text-right">Qty</th>
                                            <th className="px-2 py-1.5 text-right">Rate</th>
                                            <th className="px-2 py-1.5 text-right">Amount</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {importWorkItems.map((item: any, idx: number) => (
                                            <tr key={idx} className="border-b border-gray-100">
                                              <td className="px-2 py-1 text-gray-400">{idx + 1}</td>
                                              <td className="px-2 py-1 font-mono text-amber-700">{item.itm_cd || item.boq_itm_cd || '-'}</td>
                                              <td className="px-2 py-1 truncate max-w-[200px]">{item.itm_nm || item.boq_itm_nm}</td>
                                              <td className="px-2 py-1 text-center text-gray-500">{item.uom_cd || '-'}</td>
                                              <td className="px-2 py-1 text-right font-mono">{item.qty_nu || item.cntrct_qty_nu || item.estmt_qty_nu || 0}</td>
                                              <td className="px-2 py-1 text-right font-mono">{item.rate_at || item.cntrct_rte_at || item.estmt_rte_at || 0}</td>
                                              <td className="px-2 py-1 text-right font-mono font-semibold">
                                                ₹{((parseFloat(item.qty_nu || item.cntrct_qty_nu || item.estmt_qty_nu || 0)) * (parseFloat(item.rate_at || item.cntrct_rte_at || item.estmt_rte_at || 0))).toLocaleString('en-IN')}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    <button onClick={importAllItems}
                                      className="w-full py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 flex items-center justify-center gap-1.5">
                                      <i className="ri-download-line"></i> Import All {importWorkItems.length} Items
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        ) : itemPickerType === 'INDIVIDUAL' ? (
                          <div className="space-y-3">
                            {/* Row 1: Item No/Code + Short Description */}
                            <div className="grid grid-cols-4 gap-3">
                              <div>
                                <label className="text-[10px] font-medium text-gray-500 block mb-1">Item No / Code</label>
                                <input type="text" value={indvItem.itm_cd} onChange={(e) => setIndvItem({ ...indvItem, itm_cd: e.target.value })}
                                  placeholder="e.g., EW-001" className="w-full border rounded-lg px-3 py-2 text-sm" />
                              </div>
                              <div className="col-span-3">
                                <label className="text-[10px] font-medium text-gray-500 block mb-1">Short Description *</label>
                                <input type="text" value={indvItem.itm_nm} onChange={(e) => setIndvItem({ ...indvItem, itm_nm: e.target.value })}
                                  placeholder="Brief item name" className="w-full border rounded-lg px-3 py-2 text-sm" />
                              </div>
                            </div>

                            {/* Row 2: Long Description */}
                            <div>
                              <label className="text-[10px] font-medium text-gray-500 block mb-1">Detailed Description</label>
                              <textarea value={indvItem.itm_dscn_tx} onChange={(e) => setIndvItem({ ...indvItem, itm_dscn_tx: e.target.value })}
                                placeholder="Detailed specification, scope, and methodology..." className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} />
                            </div>

                            {/* Row 3: Unit Group + Unit + Quantity + Rate */}
                            <div className="grid grid-cols-5 gap-3">
                              <div>
                                <label className="text-[10px] font-medium text-gray-500 block mb-1">Unit Group *</label>
                                <select value={indvItem.uom_group} onChange={(e) => setIndvItem({ ...indvItem, uom_group: e.target.value, uom_cd: '' })}
                                  className="w-full border rounded-lg px-3 py-2 text-sm">
                                  <option value="">All Groups</option>
                                  <option value="LENGTH">Length</option>
                                  <option value="AREA">Area</option>
                                  <option value="VOLUME">Volume</option>
                                  <option value="WEIGHT">Weight</option>
                                  <option value="COUNT">Count</option>
                                  <option value="LUMPSUM">Lump Sum</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-medium text-gray-500 block mb-1">Unit *</label>
                                <select value={indvItem.uom_cd} onChange={(e) => setIndvItem({ ...indvItem, uom_cd: e.target.value })}
                                  className="w-full border rounded-lg px-3 py-2 text-sm">
                                  <option value="">Select Unit</option>
                                  {uoms
                                    .filter(u => !indvItem.uom_group || (u as any).uom_type_cd === indvItem.uom_group)
                                    .map(uom => (
                                      <option key={`${uom.uom_id}`} value={uom.uom_cd}>{uom.uom_nm} ({uom.uom_cd})</option>
                                    ))}
                                </select>
                              </div>
                              <div>
                                <label className="text-[10px] font-medium text-gray-500 block mb-1">Quantity</label>
                                <input type="number" step="0.01" value={indvItem.qty_nu} onChange={(e) => setIndvItem({ ...indvItem, qty_nu: e.target.value })}
                                  className="w-full border rounded-lg px-3 py-2 text-sm" />
                              </div>
                              <div>
                                <label className="text-[10px] font-medium text-gray-500 block mb-1">Rate (₹)</label>
                                <input type="number" step="0.01" value={indvItem.rate_at} onChange={(e) => setIndvItem({ ...indvItem, rate_at: e.target.value })}
                                  placeholder="0" className="w-full border rounded-lg px-3 py-2 text-sm" />
                              </div>
                              <div className="flex items-end">
                                <button
                                  onClick={() => {
                                    if (!indvItem.itm_nm) { showError('Please enter item short description'); return; }
                                    if (!indvItem.uom_cd) { showError('Please select a unit'); return; }
                                    addItemToOption({
                                      itm_cd: indvItem.itm_cd,
                                      itm_nm: indvItem.itm_nm,
                                      itm_dscn_tx: indvItem.itm_dscn_tx,
                                      itm_type_cd: 'INDIVIDUAL',
                                      uom_cd: indvItem.uom_cd,
                                      uom_nm: uoms.find(u => u.uom_cd === indvItem.uom_cd)?.uom_nm || indvItem.uom_cd,
                                      rate_at: parseFloat(indvItem.rate_at) || 0,
                                      qty_nu: parseFloat(indvItem.qty_nu) || 1
                                    });
                                    setIndvItem({ itm_cd: '', itm_nm: '', itm_dscn_tx: '', uom_group: '', uom_cd: '', qty_nu: '1', rate_at: '' });
                                  }}
                                  className="w-full bg-emerald-600 text-white rounded-lg py-2 hover:bg-emerald-700 transition-colors text-sm font-medium flex items-center justify-center gap-1"
                                >
                                  <i className="ri-add-line"></i> Add
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            {/* Search + Browse All button */}
                            <div className="flex items-center gap-2 mb-3">
                              <div className="relative flex-1">
                                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                                <input type="text"
                                  placeholder={`Quick search ${itemPickerType === 'SOR' ? 'SOR' : 'Rate Contract'} items...`}
                                  value={itemSearchTerm}
                                  onChange={(e) => setItemSearchTerm(e.target.value)}
                                  className="w-full pl-10 pr-4 border rounded-lg px-3 py-2 text-sm" />
                              </div>
                              <button
                                onClick={() => itemPickerType === 'SOR' ? setShowSORSelector(true) : setShowRCSelector(true)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 whitespace-nowrap ${
                                  itemPickerType === 'SOR'
                                    ? 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200'
                                    : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200'
                                }`}>
                                <i className="ri-fullscreen-line text-xs"></i> Browse All
                              </button>
                            </div>
                            {/* Inline results */}
                            <div className="max-h-48 overflow-y-auto border rounded-lg">
                              {itemPickerType === 'SOR' && filteredSORItems.length > 0 ? (
                                filteredSORItems.slice(0, 50).map(item => (
                                  <div key={item.sor_itm_id} onClick={() => addSORItem(item)}
                                    className="p-2 hover:bg-indigo-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                      <span className="font-medium text-sm">{item.sor_itm_nm}</span>
                                      <span className="ml-2 text-xs text-gray-500 font-mono">{item.sor_itm_cd}</span>
                                    </div>
                                    <div className="text-right flex-shrink-0 ml-3">
                                      <span className="text-sm font-medium text-emerald-700">₹{(item.rate_at || 0).toLocaleString('en-IN')}</span>
                                      <span className="ml-1 text-xs text-gray-500">/ {item.uom_nm || item.uom_cd}</span>
                                    </div>
                                  </div>
                                ))
                              ) : itemPickerType === 'RATE_CONTRACT' && filteredRCItems.length > 0 ? (
                                filteredRCItems.slice(0, 50).map(item => (
                                  <div key={item.rate_cntrct_id} onClick={() => addRCItem(item)}
                                    className="p-2 hover:bg-teal-50 cursor-pointer border-b last:border-b-0 flex items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                      <span className="font-medium text-sm">{item.rate_cntrct_nm}</span>
                                      <span className="ml-2 text-xs text-gray-500 font-mono">{item.rate_cntrct_cd}</span>
                                    </div>
                                    <div className="text-right flex-shrink-0 ml-3">
                                      <span className="text-sm font-medium text-emerald-700">₹{(item.rate_at || 0).toLocaleString('en-IN')}</span>
                                      <span className="ml-1 text-xs text-gray-500">/ {item.uom_cd}</span>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="p-4 text-center text-gray-500 text-sm">
                                  {itemSearchTerm ? 'No items found' : `Type to search or click "Browse All" to see all ${itemPickerType === 'SOR' ? 'SOR' : 'Rate Contract'} items`}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* BOQ Items Table with Item Groups */}
                    <div className="bg-white rounded-lg border overflow-hidden">
                      {/* Add Group button */}
                      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                        <span className="text-xs font-semibold text-gray-600">
                          {isGoodsWork ? 'Items to purchase' : 'Bill of Quantities (BOQ)'}
                          {wrkProcurementType !== 'WORKS' && (
                            <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                              {procurementTypeLabel(wrkProcurementType)} purchase
                            </span>
                          )}
                          {isGoodsWork && (
                            <span className="block text-[10px] font-normal text-gray-500">
                              {assetCategoriesFailed
                                ? 'Asset categories could not be loaded. Reopen the form before marking items as assets.'
                                : 'Pick an asset category for items that should be added to the asset register when the bill is paid.'}
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          {showAddGroup ? (
                            <div className="flex items-center gap-1">
                              <input type="text" value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)}
                                placeholder="Group name (e.g., Civil Works)" className="border rounded px-2 py-1 text-xs w-48"
                                onKeyDown={(e) => e.key === 'Enter' && addItemGroup()} autoFocus />
                              <button onClick={addItemGroup} disabled={!newGroupName.trim()}
                                className="px-2 py-1 bg-emerald-600 text-white rounded text-xs hover:bg-emerald-700 disabled:opacity-50">Add</button>
                              <button onClick={() => setShowAddGroup(false)} className="px-2 py-1 text-gray-500 text-xs hover:bg-gray-100 rounded">Cancel</button>
                            </div>
                          ) : (
                            <button onClick={() => setShowAddGroup(true)}
                              className="px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 rounded flex items-center gap-1">
                              <i className="ri-folder-add-line"></i> Add Group
                            </button>
                          )}
                        </div>
                      </div>

                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-8">#</th>
                            <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-24">Code</th>
                            <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-16">UOM</th>
                            <th className="px-2 py-2 text-right text-[10px] font-medium text-gray-500 uppercase w-20">Qty</th>
                            <th className="px-2 py-2 text-right text-[10px] font-medium text-gray-500 uppercase w-24">Rate</th>
                            <th className="px-2 py-2 text-right text-[10px] font-medium text-gray-500 uppercase w-28">Amount</th>
                            <th className="px-2 py-2 text-center text-[10px] font-medium text-gray-500 uppercase w-20">Move</th>
                            <th className="px-2 py-2 text-center text-[10px] font-medium text-gray-500 uppercase w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeOption.items.length === 0 ? (
                            <tr>
                              <td colSpan={9} className="px-3 py-8 text-center text-gray-500">
                                <i className="ri-file-list-3-line text-3xl mb-2 block text-gray-300"></i>
                                No items added yet. Click "Add Item" to add BOQ items.
                              </td>
                            </tr>
                          ) : (
                            <>
                              {/* Render grouped items */}
                              {(activeOption.itemGroups || []).map((group) => {
                                const groupItems = activeOption.items.filter(i => i.grp_id === group.grp_id);
                                const groupTotal = groupItems.reduce((s, i) => s + i.amount_at, 0);
                                return (
                                  <Fragment key={`grp-${group.grp_id}`}>
                                    {/* Group header row */}
                                    <tr className="bg-indigo-50 border-t-2 border-indigo-200">
                                      <td colSpan={6} className="px-2 py-2">
                                        <div className="flex items-center gap-2">
                                          <button onClick={() => toggleGroupCollapse(group.grp_id)} className="text-indigo-500 hover:text-indigo-700">
                                            <i className={`ri-arrow-${group.collapsed ? 'right' : 'down'}-s-line`}></i>
                                          </button>
                                          <i className="ri-folder-line text-indigo-500"></i>
                                          <span className="text-sm font-semibold text-indigo-800">{group.grp_nm}</span>
                                          <span className="text-[10px] text-indigo-500">({groupItems.length} items)</span>
                                        </div>
                                      </td>
                                      <td className="px-2 py-2 text-right">
                                        <span className="text-sm font-bold text-indigo-700">₹{groupTotal.toLocaleString('en-IN')}</span>
                                      </td>
                                      <td></td>
                                      <td className="px-2 py-2 text-center">
                                        <button onClick={() => removeItemGroup(group.grp_id)} className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" title="Remove group">
                                          <i className="ri-delete-bin-line text-xs"></i>
                                        </button>
                                      </td>
                                    </tr>
                                    {/* Group items */}
                                    {!group.collapsed && groupItems.map((item) => {
                                      const globalIdx = activeOption.items.indexOf(item);
                                      return (
                                        <tr key={item.itm_id} className="hover:bg-indigo-50/30 border-b border-gray-100">
                                          <td className="px-2 py-1.5 text-xs text-gray-400 pl-8">{globalIdx + 1}</td>
                                          <td className="px-2 py-1.5">
                                            <span className="text-xs font-mono">{item.itm_cd || '-'}</span>
                                            <span title={item.itm_type_cd === 'SOR' ? sorProvenance(item) : undefined}
                                              className={`ml-1 text-[9px] px-1 py-0.5 rounded ${item.itm_type_cd === 'SOR' ? 'bg-blue-50 text-blue-600' : item.itm_type_cd === 'RATE_CONTRACT' ? 'bg-purple-50 text-purple-600' : 'bg-gray-50 text-gray-500'}`}>{item.itm_type_cd === 'RATE_CONTRACT' ? 'RC' : item.itm_type_cd}</span>
                                            {item.itm_type_cd === 'SOR' && (item.sor_yr_cd || item.sor_type_nm) && (
                                              <span className="block text-[9px] text-gray-400 leading-tight">
                                                {[item.sor_yr_cd, item.sor_type_nm].filter(Boolean).join(' · ')}
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <input type="text" value={item.itm_nm} onChange={(e) => updateItem(activeOptionIdx, globalIdx, 'itm_nm', e.target.value)}
                                              className="w-full text-xs border-0 p-0 focus:ring-0 bg-transparent" placeholder="Description" />
                                            {isGoodsWork && (
                                              <GoodsItemFields categories={assetCategories}
                                                asstCtgryId={item.asst_ctgry_id} spcfcnTx={item.spcfcn_tx}
                                                onCategoryChange={(v) => updateItem(activeOptionIdx, globalIdx, 'asst_ctgry_id', v)}
                                                onSpecChange={(v) => updateItem(activeOptionIdx, globalIdx, 'spcfcn_tx', v)} />
                                            )}
                                          </td>
                                          <td className="px-2 py-1.5 text-xs text-gray-500">{item.uom_nm || item.uom_cd}</td>
                                          <td className="px-2 py-1.5">
                                            <input type="number" value={item.qty_nu} onChange={(e) => updateItem(activeOptionIdx, globalIdx, 'qty_nu', parseFloat(e.target.value) || 0)}
                                              className="w-full text-xs text-right border rounded px-1.5 py-0.5" min="0" step="0.01" />
                                          </td>
                                          <td className="px-2 py-1.5">
                                            <input type="number" value={item.rate_at} onChange={(e) => updateItem(activeOptionIdx, globalIdx, 'rate_at', parseFloat(e.target.value) || 0)}
                                              className="w-full text-xs text-right border rounded px-1.5 py-0.5" min="0" step="0.01" />
                                          </td>
                                          <td className="px-2 py-1.5 text-right text-xs font-medium">₹{item.amount_at.toLocaleString('en-IN')}</td>
                                          <td className="px-2 py-1.5 text-center">
                                            <select value={item.grp_id || ''} onChange={(e) => moveItemToGroup(globalIdx, e.target.value ? parseInt(e.target.value) : undefined)}
                                              className="text-[9px] border rounded px-1 py-0.5 w-full" title="Move to group">
                                              <option value="">Ungrouped</option>
                                              {(activeOption.itemGroups || []).map(g => <option key={g.grp_id} value={g.grp_id}>{g.grp_nm}</option>)}
                                            </select>
                                          </td>
                                          <td className="px-2 py-1.5 text-center">
                                            <button onClick={() => removeItem(activeOptionIdx, globalIdx)} className="p-0.5 text-red-400 hover:text-red-600 rounded"><i className="ri-delete-bin-line text-xs"></i></button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </Fragment>
                                );
                              })}

                              {/* Ungrouped items */}
                              {getUngroupedItems().length > 0 && (activeOption.itemGroups || []).length > 0 && (
                                <tr className="bg-gray-100 border-t-2 border-gray-300">
                                  <td colSpan={9} className="px-2 py-1.5">
                                    <div className="flex items-center gap-2">
                                      <i className="ri-file-list-3-line text-gray-400"></i>
                                      <span className="text-xs font-semibold text-gray-600">Ungrouped Items ({getUngroupedItems().length})</span>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              {getUngroupedItems().map((item) => {
                                const globalIdx = activeOption.items.indexOf(item);
                                return (
                                  <tr key={item.itm_id} className="hover:bg-gray-50 border-b border-gray-100">
                                    <td className="px-2 py-1.5 text-xs text-gray-400">{globalIdx + 1}</td>
                                    <td className="px-2 py-1.5">
                                      <span className="text-xs font-mono">{item.itm_cd || '-'}</span>
                                      <span title={item.itm_type_cd === 'SOR' ? sorProvenance(item) : undefined}
                                              className={`ml-1 text-[9px] px-1 py-0.5 rounded ${item.itm_type_cd === 'SOR' ? 'bg-blue-50 text-blue-600' : item.itm_type_cd === 'RATE_CONTRACT' ? 'bg-purple-50 text-purple-600' : 'bg-gray-50 text-gray-500'}`}>{item.itm_type_cd === 'RATE_CONTRACT' ? 'RC' : item.itm_type_cd}</span>
                                            {item.itm_type_cd === 'SOR' && (item.sor_yr_cd || item.sor_type_nm) && (
                                              <span className="block text-[9px] text-gray-400 leading-tight">
                                                {[item.sor_yr_cd, item.sor_type_nm].filter(Boolean).join(' · ')}
                                              </span>
                                            )}
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <input type="text" value={item.itm_nm} onChange={(e) => updateItem(activeOptionIdx, globalIdx, 'itm_nm', e.target.value)}
                                        className="w-full text-xs border-0 p-0 focus:ring-0 bg-transparent" placeholder="Description" />
                                      {isGoodsWork && (
                                        <GoodsItemFields categories={assetCategories}
                                          asstCtgryId={item.asst_ctgry_id} spcfcnTx={item.spcfcn_tx}
                                          onCategoryChange={(v) => updateItem(activeOptionIdx, globalIdx, 'asst_ctgry_id', v)}
                                          onSpecChange={(v) => updateItem(activeOptionIdx, globalIdx, 'spcfcn_tx', v)} />
                                      )}
                                    </td>
                                    <td className="px-2 py-1.5 text-xs text-gray-500">{item.uom_nm || item.uom_cd}</td>
                                    <td className="px-2 py-1.5">
                                      <input type="number" value={item.qty_nu} onChange={(e) => updateItem(activeOptionIdx, globalIdx, 'qty_nu', parseFloat(e.target.value) || 0)}
                                        className="w-full text-xs text-right border rounded px-1.5 py-0.5" min="0" step="0.01" />
                                    </td>
                                    <td className="px-2 py-1.5">
                                      <input type="number" value={item.rate_at} onChange={(e) => updateItem(activeOptionIdx, globalIdx, 'rate_at', parseFloat(e.target.value) || 0)}
                                        className="w-full text-xs text-right border rounded px-1.5 py-0.5" min="0" step="0.01" />
                                    </td>
                                    <td className="px-2 py-1.5 text-right text-xs font-medium">₹{item.amount_at.toLocaleString('en-IN')}</td>
                                    <td className="px-2 py-1.5 text-center">
                                      {(activeOption.itemGroups || []).length > 0 && (
                                        <select value="" onChange={(e) => e.target.value && moveItemToGroup(globalIdx, parseInt(e.target.value))}
                                          className="text-[9px] border rounded px-1 py-0.5 w-full" title="Move to group">
                                          <option value="">Move to...</option>
                                          {(activeOption.itemGroups || []).map(g => <option key={g.grp_id} value={g.grp_id}>{g.grp_nm}</option>)}
                                        </select>
                                      )}
                                    </td>
                                    <td className="px-2 py-1.5 text-center">
                                      <button onClick={() => removeItem(activeOptionIdx, globalIdx)} className="p-0.5 text-red-400 hover:text-red-600 rounded"><i className="ri-delete-bin-line text-xs"></i></button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </>
                          )}
                        </tbody>
                        {activeOption.items.length > 0 && (
                          <tfoot className="bg-emerald-50">
                            <tr className="border-t-2 border-emerald-300">
                              <td colSpan={6} className="px-3 py-3 text-right font-medium">Option Total:</td>
                              <td className="px-3 py-3 text-right">
                                <span className="text-lg font-bold text-emerald-700">₹{activeOption.totl_at.toLocaleString('en-IN')}</span>
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                  {/* Abstract Estimate */}
                  <div className="bg-indigo-50/50 p-4 rounded-lg border border-indigo-200">
                    <div className="flex items-center justify-between mb-3 px-3 py-2 bg-gradient-to-r from-indigo-600 to-violet-500 rounded-lg">
                      <div className="flex items-center gap-2">
                        <i className="ri-file-list-2-line text-white"></i>
                        <h4 className="font-medium text-white">Abstract Estimate</h4>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={generateAbstractFromBOQ}
                          className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm flex items-center gap-1 transition-colors text-white"
                        >
                          <i className="ri-magic-line"></i>
                          Generate from BOQ
                        </button>
                        <button
                          onClick={addAbstractItem}
                          className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm flex items-center gap-1 transition-colors text-white"
                        >
                          <i className="ri-add-line"></i>
                          Add Row
                        </button>
                        <button
                          onClick={() => setIsAbstractExpanded(!isAbstractExpanded)}
                          className="p-1 hover:bg-white/20 rounded-lg transition-colors text-white"
                        >
                          <i className={`${isAbstractExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`}></i>
                        </button>
                      </div>
                    </div>

                    {isAbstractExpanded && (
                      <div className="bg-white rounded-lg border overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-16">S.No</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 w-40">Amount Rs.</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 w-16">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {abstractItems.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="px-3 py-8 text-center text-gray-500">
                                  <i className="ri-file-list-2-line text-3xl mb-2 block text-gray-300"></i>
                                  No abstract items. Click "Add Row" or "Generate from BOQ".
                                </td>
                              </tr>
                            ) : (
                              abstractItems.map((item, idx) => (
                                <tr key={item.abstrt_itm_id} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-sm text-gray-500">{idx + 1}</td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="text"
                                      value={item.dscn_tx}
                                      onChange={(e) => updateAbstractItem(idx, 'dscn_tx', e.target.value)}
                                      className="w-full text-sm border rounded px-2 py-1 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                      placeholder="Enter description"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <input
                                      type="number"
                                      value={item.amount_at}
                                      onChange={(e) => updateAbstractItem(idx, 'amount_at', parseFloat(e.target.value) || 0)}
                                      className="w-full text-sm text-right border rounded px-2 py-1 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                      min="0"
                                      step="0.01"
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <button
                                      onClick={() => removeAbstractItem(idx)}
                                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                          {abstractItems.length > 0 && (
                            <tfoot className="bg-indigo-50">
                              <tr>
                                <td colSpan={2} className="px-3 py-3 text-right font-medium">
                                  Abstract Total:
                                </td>
                                <td className="px-3 py-3 text-right">
                                  <span className="text-lg font-bold text-indigo-700">
                                    ₹{abstractTotal.toLocaleString('en-IN')}
                                  </span>
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Remarks */}
                  <div className="bg-white rounded-lg border p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remarks / Notes
                    </label>
                    <textarea
                      value={rmrksTx}
                      onChange={(e) => setRmrksTx(e.target.value)}
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-emerald-500"
                      placeholder="Any additional remarks..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={`border-t px-6 py-4 bg-gray-50 flex items-center justify-between ${
            isFullscreen ? '' : 'rounded-b-xl'
          }`}>
            <div className="flex items-center gap-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <div className="text-sm text-gray-500">
                {options.length} option(s) | {options.reduce((sum, o) => sum + o.items.length, 0)} BOQ item(s)
                {abstractItems.length > 0 && (
                  <span className="ml-1">| {abstractItems.length} abstract item(s) ₹{abstractTotal.toLocaleString('en-IN')}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-gray-500">Grand Total</div>
                <div className="text-xl font-bold text-emerald-700">
                  ₹{grandTotal.toLocaleString('en-IN')}
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={saving || blockOriginalDuplicate}
                title={blockOriginalDuplicate ? 'An ORIGINAL estimate already exists for this work — switch to Revised/Supplementary or edit the existing one.' : undefined}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    {isEdit ? 'Update Estimate' : 'Save Estimate'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* SOR Selector Modal */}
      <SORSelector
        isOpen={showSORSelector}
        onClose={() => setShowSORSelector(false)}
        onSelect={handleSORSelect}
        excludeIds={options[activeOptionIdx]?.items.filter(i => i.sor_itm_id).map(i => i.sor_itm_id!) || []}
      />
      {/* Rate Contract Selector Modal */}
      <RateContractSelector
        isOpen={showRCSelector}
        onClose={() => setShowRCSelector(false)}
        onSelect={handleRCSelect}
        excludeIds={options[activeOptionIdx]?.items.filter(i => i.rate_cntrct_id).map(i => i.rate_cntrct_id!) || []}
      />

      {/* Proposal Attach Picker */}
      {showProposalPicker && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowProposalPicker(false)}></div>
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl">
              <div className={`${WMS_POPUP_HEADER} rounded-t-xl`}>
                <div className="flex items-center gap-2 min-w-0">
                  <i className="ri-stack-line text-base text-white/90"></i>
                  <div className="min-w-0">
                    <h2 className={WMS_POPUP_TITLE}>Attach Proposals</h2>
                    <p className={WMS_POPUP_SUBTITLE}>Select one or more proposals to link to this work</p>
                  </div>
                </div>
                <button onClick={() => setShowProposalPicker(false)} className={WMS_POPUP_CLOSE}>
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="px-6 py-3 border-b">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={proposalPickerSearch}
                    onChange={(e) => setProposalPickerSearch(e.target.value)}
                    placeholder="Search proposals by code or name..."
                    className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  {proposalPickerResults.length} result{proposalPickerResults.length !== 1 ? 's' : ''}
                  {proposalPickerSelected.size > 0 && (
                    <span className="ml-2 text-purple-700 font-medium">· {proposalPickerSelected.size} selected</span>
                  )}
                </p>
              </div>
              <div className="max-h-[50vh] overflow-y-auto">
                {proposalPickerLoading ? (
                  <ListSkeleton />
                ) : proposalPickerResults.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <i className="ri-inbox-line text-4xl mb-2 block opacity-30"></i>
                    <p className="font-medium">No proposals found</p>
                  </div>
                ) : (
                  <ul className="p-4 space-y-2">
                    {proposalPickerResults.map((p) => {
                      const alreadyAttached = attachedProposals.some((a) => a.prpsl_id === p.prpsl_id);
                      const checked = proposalPickerSelected.has(p.prpsl_id);
                      return (
                        <li key={p.prpsl_id}>
                          <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${alreadyAttached ? 'opacity-60 bg-gray-50' : checked ? 'border-purple-500 bg-purple-50' : 'hover:border-purple-300'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={alreadyAttached}
                              onChange={(e) => {
                                setProposalPickerSelected((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(p.prpsl_id); else next.delete(p.prpsl_id);
                                  return next;
                                });
                              }}
                              className="mt-1 accent-purple-600"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm text-purple-700 bg-purple-100 px-2 py-0.5 rounded">{p.prpsl_cd}</span>
                                {alreadyAttached && <span className="text-xs text-gray-500 italic">already attached</span>}
                              </div>
                              <p className="font-medium text-gray-900 truncate">{p.prpsl_nm}</p>
                              <p className="text-sm text-gray-500">
                                Estimated: <span className="font-medium text-emerald-700">₹{(p.estmt_cst_at || 0).toLocaleString('en-IN')}</span>
                              </p>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowProposalPicker(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >Cancel</button>
                <button
                  type="button"
                  onClick={handleAttachProposals}
                  disabled={proposalPickerSelected.size === 0}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Attach {proposalPickerSelected.size > 0 ? `(${proposalPickerSelected.size})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
